import { cellAtWorld, cellCenter } from '../grid.ts';
import { dist2, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import { enemiesNear, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { wedgeLayout } from '../battle/wedge-layout.ts';
import { wedgeOrder } from '../battle/wedges.ts';
import { hookOrder, ropeLive } from '../battle/hooks.ts';
import { gustLee, gustOrder } from '../battle/gusts.ts';
import { weightOrder } from '../battle/weights.ts';
import { weightLayout } from '../battle/weight-layout.ts';
import { roofFarCount, roofHung, roofLoads, roofOrder, roofTakes, roofYard, ROOF_HEARTH } from '../battle/roof.ts';
import { slideOrder, slideRocks, slideShadow, snowAt } from '../battle/slide.ts';
import { avalancheLayout } from '../battle/slide-layout.ts';
import { cosA, iatan2, sinA } from '../math/fixed.ts';
import { laneObvious } from '../battle/wedge-layout.ts';
import { geo, heroWalkable } from '../grid.ts';
import { idiv, imax, imin, isqrt } from '../math/fixed.ts';
import { dungeonBot } from './dungeon-bot.ts';
import { LINE_NAMES, sec } from '@somnhold/content';
import { cragWarn } from '../battle/crag.ts';
import { LINE_ANSWER, beamWays, passClear, passFlow } from '../battle/pass-layout.ts';
import { stormRocks } from '../battle/storm.ts';
import { lineOrder, LINE_SETTLE, LINE_TAKE } from '../battle/line.ts';
import { beamLoad, beamOrder, BEAM_PLACE } from '../battle/beam.ts';
import { stormOrder, stormSpot, STORM_BANNER, STORM_LIFT, STORM_ROCK, STORM_SHIELD, STORM_SLOT, STORM_STONE } from '../battle/storm.ts';
import { findEnemy } from '../battle/combat.ts';
import { useSkills } from './ghost.ts';
import { canalBurnt, canalOrder } from '../battle/canals.ts';
import { inCone } from '../battle/kindle.ts';
import { saltOrder, saltPoolOf, saltSeen, saltThrow } from '../battle/salt.ts';
import { saltLayout, sluiceFlow, sluiceSolve } from '../battle/salt-layout.ts';
import { archOrder, archSideOn } from '../battle/arch.ts';
import { archLayout } from '../battle/arch-layout.ts';
import { monCovered, monOrder, monSeen } from '../battle/monastery.ts';
import { monasteryLayout } from '../battle/monastery-layout.ts';
import { segDist2, sevOnLine, sevOrder, sevSeen, SEV_GATE, SEV_RUNNEL } from '../battle/seventh.ts';
import { golemOf, inArc, turnOf } from '../battle/golems.ts';
import { gearAt, gearOrder } from '../battle/gears.ts';
import { gearSolve } from '../battle/taller-layout.ts';
import type { GolemUnit } from '../types.ts';
import { gargAt } from '../battle/garg.ts';
import { boxAt, boxLever, boxOrder } from '../battle/gearbox.ts';
import { gondolaHolds } from '../battle/gondola.ts';
import { boxSolve } from '../battle/cable-layout.ts';
import { camsBot, emberBot, ventBot } from './senda-bot-corazon.ts';
import { cartsBot, drillsBot, strutsBot } from './senda-bot-ojos.ts';
import { kneesBot, platesBot, shellBot, tornoBot } from './senda-bot-tormo.ts';
import { cazaBot } from './caza-bot.ts';
import { clericBot } from './senda-bot-cleric.ts';
import { druidBot } from './senda-druid-bot.ts';
import { summerBot } from './senda-druid2-bot.ts';
import { autumnBot } from './senda-druid3-bot.ts';
import { winterBot } from './senda-druid4-bot.ts';
import { deepBot } from './senda-druid5-bot.ts';
import { cairnsBot, echoBot, fallenBot } from './senda-bot-aster.ts';
import { cliffBot } from './senda-bot-galo.ts';
import { magoBot } from './mago-bot.ts';

/**
 * A path's fight played by a bot (docs/SENDAS.md, «Calibración»: the lab's control, `tools/balance` senda-<cls>-<ch>):
 * one that knows the act's rule and one that doesn't (`rules.senda.bot`). The rule of each act of the warrior's
 * chapter 1 (docs/sendas/guerrero.md):
 *
 * - the pines (`field` with work): Kel digs only with the hero beside him; who knows stays by Kel and strikes what
 *   comes for him; who doesn't chases whatever is nearest;
 * - the guard house (`descent`): the crypt's own bot, Kel behind;
 * - the ledge (`wedge`): who knows plants the three shields that send the column under the cornice, waits in the nook
 *   for the counter and pushes the rock, then holds the way to the house; who doesn't plants the three gaps in front
 *   of the house (the obvious way) and holds the house.
 *
 * And of chapter 2:
 *
 * - the hooks (`hooks`): who knows reads the ropes and goes to cut the one with the most shakes it can reach before
 *   its climbers show (the ogre's always), and otherwise keeps what came over off Kel; who doesn't stays by Kel.
 * - the windbreak (`gusts`): who knows works out where Kel will be when the next gust strikes and plants a cover on
 *   its upwind side there, pulling one left behind when its hands are empty; who doesn't keeps the crows off Kel.
 * - the counterweight (`weights`): who knows hangs the pieces as the posts' one answer says, and scares the serpent
 *   off when it comes up; who doesn't counts pan by pan (each pan its own post, the cross pull forgotten).
 *
 * And of chapter 3:
 *
 * - the climb (`slope`): both keep to the train and step out of a rock's shadow; who knows fights the troll from the
 *   torch's far side, so it stands on the fire (and calls it there when it strays); who doesn't fights it where it is.
 * - the second night (`roof` with Fermín): who knows holds the hearth's yard and lights the fire again; who doesn't
 *   goes out into the far yard after them, where Fermín's roof comes down.
 * - the roof (`roof`, the test): who knows keeps the hearth's eave light, tips the far slope with a piece when the troll
 *   or a crowd is under it (or before the hearth's would tip), and strikes the troll while it lies buried; who doesn't
 *   never touches the hooks, and keeps the hearth and fights the troll.
 *
 * And of chapter 4:
 *
 * - the loose slope (`slide`, act 1): both keep Kel and the train; who knows runs for a rock's lee when a front's
 *   shadow shows (the cell straight below a rock nearest it) and waits there; who doesn't stays out with Kel.
 * - the ice cave (`cave`): who knows gets round a sentinel facing it (to its back, else its side) before it strikes;
 *   who doesn't strikes it where it stands, from the front.
 * - the large wedges (`slide` with lanes, the test): who knows drives the four wedges of the slope's one answer, top
 *   row first, then waits for the fall where no snow comes down, fighting what reaches it; who doesn't drives the
 *   obvious four (down the middle: `laneObvious`) and waits the same.
 *
 * And of chapter 5:
 *
 * - the ledge of the wind (`crag`): both keep up with Harl and strike the cubs; who knows steps into the nearest niche of
 *   the wall when the wind warns; who doesn't stays out on the ledge by Harl.
 * - the camp (`line`): both clear it by Harl; then who knows lays Ruy's line as the pages say, striking the cubs that
 *   come for it; who doesn't sets the shields in the order they fell, Ruy left of Harl «because he is the captain», and
 *   says «así queda».
 * - the beam (`beam`): who knows keeps the basket at the number less one, changing stones (not only adding) as the
 *   number goes up, and strikes the cubs on the basket first; who doesn't only adds a stone when the basket is light.
 * - the Ventisquero (`storm`): who knows drives a wedge into a footprint before each charge (fetching more from the
 *   pile), wedges the avalanche's right two rocks when it is called, sets the beam's stone in its breath, sets Harl's
 *   shield, lifts the banner and the fallen, strikes the cubs near the line and the giant when it is exposed; who
 *   doesn't fights the giant and the cubs that come at it.
 *
 *
 * And the paladin's chapter 1 (docs/sendas/paladin.md):
 *
 * - the canals (`canal`, small): who knows follows the canals from each fork and moves every stone that sends the
 *   water back to the sea, then keeps the crías off Marit; who doesn't tries: after each wave lost it moves a stone
 *   (the next by the waves lost), and keeps Marit the same.
 * - the second altar (`kindle`): both keep what comes off the altar and Marit; who knows stands by Marit when the
 *   drake's cone is marked on her (it takes the fire for her); who doesn't keeps striking where it is.
 * - the water that goes (`canal`, the test): both set the inlet's stone and keep off the plough's lines; who knows sends
 *   the great waves up while the high pool isn't full and its mouth is open, shuts the mouth once it is, and the rest
 *   down to the low one; who doesn't fills the low one first (it is nearer) and never shuts the mouth.
 *
 * And its chapter 2, the salt flats:
 *
 * - the fog (`salt`, fog): who knows walks Marit along the dykes (what comes at her there shows) and waits for her;
 *   who doesn't walks her straight across the pools.
 * - the gates (`salt`, sluice): who knows turns the short way's gates (worked out from the marks, as on paper) at
 *   the start of each tide; who doesn't opens the altar's gate down first (the obvious one), then the rest in a row.
 * - the test (`salt`, hold): who knows reads the tracks on the far dry pools and dykes, has Marit throw her salt on
 *   those ways' dykes before they come (where it lasts), salts the old stalker by Marit after it leaps and stands on
 *   its mark; who doesn't has her throw it by the altar (in the mud, where it melts) and stays at the altar.
 *
 * And its chapter 3, the bridge's altar:
 *
 * - the two ends (`arch`, ends): who knows fights at the end a band comes up and, with both ends busy, stands beside
 *   the altar (its aura over both); who doesn't stays by Marit at the lower end.
 * - the gutters (`arch`, gutters): who knows opens the gutter where it rains and shuts the other as the rain moves,
 *   and clears what stands on the rain's gutter first; who doesn't opens both (to catch all the rain).
 * - the night (`arch`, vigil): both keep the gutters right. Who knows saves the water: it sweeps a band only with the
 *   cistern full and time to fill it again before the next mammoth, waits at the mammoth's gargoyle as it nears the
 *   top and opens it when its head goes down; who doesn't sweeps every band that comes up (the obvious).
 *
 * And its chapter 4, the monastery of the waterfall:
 *
 * - the memory at the door (`monastery`, porch): who knows fights at Ser Edda's side (her cover); who doesn't goes down
 *   the gorge to meet the Marea (the obvious: stop them before the door).
 * - the cells (`monastery`, cells): who knows salts both crossings, reads which arm the prints come from, salts that
 *   arm past its far cell, reads whether the next group walks through it (the far cell) or not (the near one) and shuts
 *   that door; who doesn't salts the chapel's door (they come, it shows, not from where), keeps the altar and tries the
 *   doors one by one, one for each group that comes out.
 * - the cloister (`monastery`, cloister, the test): who knows opens the orchards and the kitchen first and keeps the
 *   siege off Marit, shuts the kitchen at its sixth mark, opens the ravine only when the siege has gone quiet, and waits
 *   for the great sentinel at the chapel's door; who doesn't opens the ravine first (it drains fastest) and fights what
 *   is nearest.
 *
 * It only moves the hero and picks its target, as a player's commands would; nothing it does is outside the rules.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/** Strikes `t` (its skills too); the ultimate when three or more stand round it. */
const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

/** Fights what is within `r` of a point (nearest to the point first); false if nothing is. */
const guard = (s: BattleState, h: Hero, x: number, y: number, r: number, ok: (e: Enemy) => boolean = () => true): boolean => {
  const near = enemiesNear(s, x, y, r, (e) => targetable(s, e) && ok(e));
  const t = nearestTo(near, x, y);
  if (!t) return false;
  strike(s, h, t);
  return true;
};

/** The pines: by the person at work (who knows), or after the nearest (who doesn't). */
const fieldBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const st = s.senda;
  const r = s.rules?.senda;
  if (!st || !r) return;
  const i = r.npcs.findIndex((n) => n.does === 'work');
  const kel = st.npcs[i] as SendaNpc | undefined;
  if (!knows || !kel || kel.hp <= 0) {
    const all = enemiesNear(s, h.x, h.y, 14000, (e) => targetable(s, e));
    const t = nearestTo(all, h.x, h.y);
    if (t) strike(s, h, t);
    else h.target = 0;
    return;
  }
  // What comes for Kel first; past that, stay by him (within his reach for the work).
  if (guard(s, h, kel.x, kel.y, 2600)) return;
  h.target = 0;
  const spot = r.npcs[i]?.spots[kel.spot] ?? { x: kel.x, y: kel.y };
  go(s, h, spot.x + 500, spot.y);
};

/** The layout the ledge was built from (its seed is the battle's): the bot reads its answer, as one who knows it. */
const LEDGE = new WeakMap<BattleState, ReturnType<typeof wedgeLayout>>();
const ledgeOf = (s: BattleState): ReturnType<typeof wedgeLayout> => {
  let L = LEDGE.get(s);
  if (!L) {
    L = wedgeLayout(s.seed);
    LEDGE.set(s, L);
  }
  return L;
};

/** When the knowing bot sends the hero to the rock: the strip this full (of the counter's goal), in basis points. */
export const WEDGE_BOT = { pushAtBp: 10000 };

const wedgeBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.wedge;
  const w = s.senda?.wedge;
  if (!r || !w) return;
  const L = ledgeOf(s);
  const R = s.map.radius;
  const want = knows ? L.solution : L.front;
  /** Strikes only what is already in reach (a step would call the work off). */
  const still = (): void => {
    const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range + 200, (e) => targetable(s, e)), h.x, h.y);
    h.target = t ? t.id : 0;
    h.forced = false;
  };
  // 1) The shields: each wanted gap shut, in turn, a wrong one pulled.
  const wrong = w.shut.findIndex((x, i) => x === 1 && !want.includes(i));
  const todo = want.find((i) => w.shut[i] !== 1);
  const job = wrong >= 0 ? wrong : w.hand > 0 ? todo : undefined;
  if (job !== undefined) {
    if (w.at !== job) wedgeOrder(s, h, job);
    else still();
    return;
  }
  // The gap the strip comes down to, and the yard's side of it (where the column comes out).
  const last = r.strip[r.strip.length - 1] as number;
  const exitGap = r.gaps.find((g) => {
    const a = cellCenter(R, g);
    const b = cellCenter(R, last);
    return withinRange(a.x, a.y, b.x, b.y, 1100);
  });
  const out = exitGap !== undefined ? cellCenter(R, exitGap) : cellCenter(R, last);
  const inside = { x: out.x - (out.x >> 2), y: out.y - (out.y >> 2) };
  if (knows && w.pushed < 0) {
    // 2) Into the nook before the column (only a hero sent to the rock pushes it), then the push as the strip fills.
    const n = cellCenter(R, r.nook);
    if (w.at === r.gaps.length) {
      still();
      return;
    }
    if (!withinRange(h.x, h.y, n.x, n.y, 400)) {
      go(s, h, n.x, n.y, 200);
      h.target = 0;
      return;
    }
    if (w.count * 10000 >= r.need * WEDGE_BOT.pushAtBp) {
      wedgeOrder(s, h, r.gaps.length);
      return;
    }
    still();
    return;
  }
  // 3) The way to the house: who knows holds the strip's way out; who doesn't, the door.
  const at = knows ? inside : { x: 0, y: 0 };
  if (guard(s, h, at.x, at.y, knows ? 4200 : 3200)) return;
  h.target = 0;
  go(s, h, at.x, at.y);
};

/** The person by name on the field (Kel). */
const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** Fights what comes for Kel (within `r` of him), else what is within reach of the hero; false if nothing is. */
const keepKel = (s: BattleState, h: Hero, r = 3000): boolean => {
  const kel = npc(s, 'kel');
  if (kel && kel.hp > 0 && guard(s, h, kel.x, kel.y, r)) return true;
  return guard(s, h, h.x, h.y, 2600);
};

/** The hooks: the rope worth cutting it can reach in time (most shakes first), else Kel. */
const hooksBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.hooks;
  const w = s.senda?.hooks;
  const kel = npc(s, 'kel');
  if (!r || !w) return;
  if (knows) {
    let best = -1;
    let bestScore = -1;
    for (const x of w.ropes) {
      if (!ropeLive(r, x)) continue;
      const p = r.plan[x.i] as (typeof r.plan)[number];
      const a = r.anchors[p.at] as { x: number; y: number };
      const left = p.t + p.warn - s.waveT + x.out * r.climb;
      const steps = idivUp(isqrt((a.x - h.x) * (a.x - h.x) + (a.y - h.y) * (a.y - h.y)) * 12, h.stats.speed) + r.cut;
      // The ogre's rope is always worth it while he's still on it; the others only if it gets there first.
      if (p.shakes < 3 && steps > left + 6) continue;
      const score = p.shakes * 1000 - steps;
      if (score > bestScore) {
        bestScore = score;
        best = p.at;
      }
    }
    // Something on Kel right now comes first, unless the rope is the ogre's.
    const onKel = kel && kel.hp > 0 && enemiesNear(s, kel.x, kel.y, 1400, (e) => targetable(s, e)).length > 0;
    if (best >= 0 && (bestScore >= 3000 || !onKel)) {
      if (w.at !== best) hookOrder(s, h, best);
      h.target = 0;
      return;
    }
  }
  if (keepKel(s, h)) return;
  h.target = 0;
  if (kel) go(s, h, kel.x - 700, kel.y + 600);
};

const idivUp = (a: number, b: number): number => idiv(a + b - 1, b);

/** Where Kel stands `t` ticks from now if nothing stops him (straight on toward the end). */
const kelAhead = (s: BattleState, t: number): { x: number; y: number } | null => {
  const r = s.rules?.senda?.gust;
  const w = s.senda?.gust;
  const kel = npc(s, 'kel');
  if (!r || !w || !kel) return null;
  const go = imax(0, t - w.gather) * r.kel;
  const dx = r.to.x - kel.x;
  const dy = r.to.y - kel.y;
  const d = isqrt(dx * dx + dy * dy);
  if (d <= go || d === 0) return { x: r.to.x, y: r.to.y };
  return { x: kel.x + idiv(dx * go, d), y: kel.y + idiv(dy * go, d) };
};

/** The windbreak: a cover upwind of where Kel will be when the next gust strikes (and the one after). */
const gustBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.gust;
  const w = s.senda?.gust;
  if (!r || !w) return;
  if (knows) {
    if (w.job >= 0) {
      // On the way to plant or pull: strike only what is in reach.
      const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range + 150, (e) => targetable(s, e)), h.x, h.y);
      h.target = t ? t.id : 0;
      h.forced = false;
      return;
    }
    const R = s.map.radius;
    const nb = geo(R).nb;
    for (const k of [0, 1]) {
      const g = r.plan[w.next + k];
      if (!g) break;
      const at = kelAhead(s, g.t - s.waveT);
      if (!at) break;
      const cell = cellAtWorld(s, at.x, at.y);
      if (cell < 0 || gustLee(s, g.dir).includes(cell)) continue;
      // The cells whose lee takes it in, nearest the hero first.
      const back = g.dir > 0 ? [3, 4, 2] : [0, 1, 5];
      const cand: number[] = [];
      const a = nb[cell * 6 + (back[0] as number)] as number;
      if (a >= 0) cand.push(a, nb[a * 6 + (back[0] as number)] as number);
      for (const d of [back[1], back[2]] as number[]) {
        const b = nb[cell * 6 + d] as number;
        if (b >= 0) cand.push(b, nb[b * 6 + (back[0] as number)] as number);
      }
      const ok = cand.filter((c) => c >= 0 && heroWalkable(s, c) && !r.fixed.includes(c) && !w.cells.includes(c));
      if (!ok.length) continue;
      if (w.planks + w.shields <= 0) {
        if (pullBehind(s, h, 99999)) return;
        break;
      }
      const best = ok.sort((x, y) => {
        const px = cellCenter(R, x);
        const py = cellCenter(R, y);
        return dist2(h.x, h.y, px.x, px.y) - dist2(h.x, h.y, py.x, py.y) || x - y;
      })[0] as number;
      gustOrder(s, h, best);
      return;
    }
    // Nothing to plant now: a cover Kel left behind, near at hand, back in the hands.
    if (pullBehind(s, h, 7000)) return;
  }
  if (keepKel(s, h, 3200)) return;
  h.target = 0;
  const kel = npc(s, 'kel');
  if (kel) go(s, h, kel.x + 600, kel.y - 300);
};

/** Pulls the planted cover nearest the hero of those Kel has left two rows behind (within `near`); false if none. */
const pullBehind = (s: BattleState, h: Hero, near: number): boolean => {
  const w = s.senda?.gust;
  const kel = npc(s, 'kel');
  if (!w || !kel) return false;
  const R = s.map.radius;
  let best = -1;
  let bd = near * near;
  for (const c of w.cells) {
    const p = cellCenter(R, c);
    if (p.y - kel.y < 1700) continue;
    const d = dist2(h.x, h.y, p.x, p.y);
    if (d < bd) {
      bd = d;
      best = c;
    }
  }
  if (best < 0) return false;
  gustOrder(s, h, best);
  return true;
};

const WEIGHT_PLAN = new WeakMap<BattleState, number[]>();

/** Where each piece goes: the posts' answer (who knows), or pan by pan, each its own post (who doesn't). */
const weightPlan = (s: BattleState, knows: boolean): number[] => {
  let p = WEIGHT_PLAN.get(s);
  if (p) return p;
  const r = s.rules?.senda?.weight;
  if (!r) return [];
  if (knows) p = (weightLayout(s.seed).ways[0] ?? []).slice();
  else {
    // Pan by pan: the heaviest pieces first into the pan with the most room under its post; what's left on the middle.
    p = r.pieces.map(() => 3);
    const room = r.posts.slice();
    const order = r.pieces.map((x, i) => ({ i, w: x.w, stone: x.stone })).sort((a, b) => Number(a.stone) - Number(b.stone) || b.w - a.w || a.i - b.i);
    for (const x of order) {
      let best = -1;
      for (let k = 0; k < 3; k++) if ((room[k] as number) >= x.w && (best < 0 || (room[k] as number) > (room[best] as number))) best = k;
      if (best < 0 && !x.stone) best = 1;
      if (best >= 0) {
        p[x.i] = best;
        room[best] = (room[best] as number) - x.w;
      }
    }
  }
  WEIGHT_PLAN.set(s, p);
  return p;
};

/** The counterweight: the pieces where the plan says, the serpent scared off when it comes up. */
const weightBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.weight;
  const w = s.senda?.weight;
  if (!r || !w) return;
  const carrying = w.on.includes(-2);
  // The serpent up by a pan, hands free: at it.
  const snake = w.snake ? s.enemies.find((e) => e.id === w.snake && e.hp > 0 && e.phase === 2) : undefined;
  if (snake && !carrying) {
    w.job = -1;
    strike(s, h, snake);
    return;
  }
  if (w.job >= 0) {
    h.target = 0;
    return;
  }
  const plan = weightPlan(s, knows);
  for (let i = 0; i < r.pieces.length; i++) {
    const want = plan[i] ?? 3;
    const on = w.on[i] as number;
    if (on === -3 || on === -4) continue;
    if (on === -2 || (want === 3 ? on >= 0 : on !== want)) {
      weightOrder(s, h, i, want);
      return;
    }
  }
  if (keepKel(s, h, 2600)) return;
  h.target = 0;
  go(s, h, r.pile.x + 900, r.pile.y - 900);
};

/** Out of a rock's shadow (the climb): true if it had to step aside. */
const dodge = (s: BattleState, h: Hero): boolean => {
  const w = s.senda?.slope;
  const r = s.rules?.senda?.slope;
  if (!w || !r) return false;
  for (const x of w.rocks) {
    if (!withinRange(h.x, h.y, x.x, x.y, r.r + 250)) continue;
    const dx = h.x - x.x;
    const dy = h.y - x.y;
    const d = isqrt(dx * dx + dy * dy) || 1;
    const k = r.r + 700;
    orderMove(s, h, x.x + idiv(dx * k, d) + (d < 50 ? k : 0), x.y + idiv(dy * k, d));
    h.target = 0;
    return true;
  }
  return false;
};

const slopeBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const w = s.senda?.slope;
  const r = s.rules?.senda?.slope;
  if (!w || !r) return;
  if (dodge(s, h)) return;
  const troll = w.troll ? s.enemies.find((e) => e.id === w.troll && e.hp > 0) : undefined;
  if (troll && w.awake) {
    if (knows) {
      // At the torch: the troll that comes for the hero stands in the fire.
      const sx = r.torch.x;
      const sy = r.torch.y + 150;
      const onFire = withinRange(troll.x, troll.y, r.torch.x, r.torch.y, r.burn + 300);
      if (!withinRange(h.x, h.y, sx, sy, 500) && !onFire) {
        go(s, h, sx, sy, 250);
        // Called onto the hero (the taunt), it walks over the fire.
        if (h.skillCd[0] === 0 && withinRange(h.x, h.y, sx, sy, 900)) castSkill(s, h, { t: 'skill', slot: 0, x: troll.x, y: troll.y, id: troll.id });
        h.target = 0;
        return;
      }
      // Wolves on the train first, then the troll.
      const kel = npc(s, 'kel');
      if (kel && guard(s, h, kel.x, kel.y, 1300)) return;
      strike(s, h, troll);
      return;
    }
    // Who doesn't know stands in front of the train and meets the troll there.
    const kel = npc(s, 'kel');
    if (kel && !withinRange(troll.x, troll.y, h.x, h.y, 1100)) {
      go(s, h, kel.x, kel.y - 500, 250);
      h.target = 0;
      return;
    }
    strike(s, h, troll);
    return;
  }
  const mule = npc(s, 'mule');
  if (mule && guard(s, h, mule.x, mule.y, 3200)) return;
  if (keepKel(s, h, 3200)) return;
  h.target = 0;
  const kel = npc(s, 'kel');
  const next = r.way[imin(r.way.length - 1, w.at)];
  if (kel && next) go(s, h, (kel.x + next.x) >> 1, ((kel.y + next.y) >> 1) - 500);
};

/** The fort's night as Ruy (who knows holds the hearth's yard), or the test (who knows works the roof). */
const roofBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.roof;
  const w = s.senda?.roof;
  if (!r || !w) return;
  const troll = w.troll ? s.enemies.find((e) => e.id === w.troll && e.hp > 0) : undefined;
  // The fire out: light it (unless the troll stamps on it: at the troll).
  if (!w.lit) {
    if (guard(s, h, r.hearth.x, r.hearth.y, 1300, (e) => e.id !== w.troll)) return;
    if (w.job !== -3) roofOrder(s, h, ROOF_HEARTH, 0);
    h.target = 0;
    return;
  }
  if (r.fermin) {
    // The memory: the hearth's yard and the passage's mouth (who knows), or the far yard after them (who doesn't).
    const at = knows ? { x: (r.hearth.x + (r.route[6] as { x: number }).x) >> 1, y: (r.hearth.y + (r.route[6] as { y: number }).y) >> 1 } : r.farMid;
    if (guard(s, h, at.x, at.y, knows ? 3000 : 4500)) return;
    h.target = 0;
    go(s, h, at.x, at.y);
    return;
  }
  if (knows && roofPlay(s, h, r, w, troll)) return;
  // Buried: at it.
  if (troll && (w.buried > 0 || troll.stun > 0)) {
    strike(s, h, troll);
    return;
  }
  if (!knows && troll && withinRange(troll.x, troll.y, r.hearth.x, r.hearth.y, 4000)) {
    strike(s, h, troll);
    return;
  }
  // Keep the hearth's yard (out of the troll's way when it isn't buried).
  if (guard(s, h, r.hearth.x, r.hearth.y, 3200)) return;
  h.target = 0;
  const home = { x: r.hearth.x + idiv(r.hearth.x - r.farMid.x, 8), y: r.hearth.y - 900 };
  go(s, h, home.x, home.y);
};

/** Works the roof: true if it gave the hero a job. */
const roofPlay = (s: BattleState, h: Hero, r: NonNullable<NonNullable<BattleState['rules']>['senda']>['roof'] & object, w: NonNullable<NonNullable<BattleState['senda']>['roof']>, troll: Enemy | undefined): boolean => {
  if (w.job >= 0) {
    const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range + 150, (e) => targetable(s, e)), h.x, h.y);
    h.target = t ? t.id : 0;
    h.forced = false;
    return true;
  }
  const [L0, L1] = roofLoads(r, w);
  const lee = (r.wind[w.wind] as { to: number }).to;
  const next = r.wind[w.wind + 1];
  const soonHearth = lee === 0 || (!!next && next.to === 0 && next.t - s.waveT < r.warn);
  const hung = roofHung(r, w);
  const trollFar = !!troll && troll.stun <= 0 && roofYard(s, troll.x, troll.y) === 1;
  const crowd = roofFarCount(s) >= 5;
  const danger = lee === 0 && L0 >= r.tip - 2;
  // A piece that tips the far slope now.
  const deep = w.snow[1] + w.snow[0] >= r.bury;
  if (((trollFar || crowd) && deep) || (danger && w.snow[1] + w.snow[0] > 0)) {
    for (let i = 0; i < r.pieces.length; i++) {
      const on = w.on[i] as number;
      if (on === 1) continue;
      const wt = (r.pieces[i] as { w: number }).w;
      if (L1 + wt >= r.tip && roofTakes(r, w, i, 1)) {
        roofOrder(s, h, i, 1);
        return true;
      }
    }
  }
  // The hearth's eave light: its pieces off (onto the far eave if it won't tip it, else the pile).
  for (let i = 0; i < r.pieces.length; i++) {
    if (w.on[i] !== 0) continue;
    if (L0 < r.tip - 4 && !soonHearth && !danger) break;
    const wt = (r.pieces[i] as { w: number }).w;
    roofOrder(s, h, i, L1 + wt < r.tip - 1 && hung.w[1] + wt <= r.cap - 4 && roofTakes(r, w, i, 1) ? 1 : 2);
    return true;
  }
  // The far slope about to tip on nobody: a piece off it, to keep its snow for when they come.
  if (lee === 1 && L1 >= r.tip - 1 && !(trollFar && deep) && roofFarCount(s) < 3 && hung.n[1] > 0) {
    let best = -1;
    for (let i = 0; i < r.pieces.length; i++) if (w.on[i] === 1 && (best < 0 || (r.pieces[i] as { w: number }).w < (r.pieces[best] as { w: number }).w)) best = i;
    if (best >= 0) {
      roofOrder(s, h, best, 2);
      return true;
    }
  }
  // With the wind on the hearth's side, keep the far slope ready: its hooks up to a tip's worth short of the snow.
  if (soonHearth && L1 < r.tip - 3) {
    for (let i = 0; i < r.pieces.length; i++) {
      if (w.on[i] !== -1) continue;
      const wt = (r.pieces[i] as { w: number }).w;
      // (Hooks kept to five: a shield of four on top still fits under the eave's hold, to tip it at will.)
      if (L1 + wt <= r.tip - 2 && hung.w[1] + wt <= r.cap - 4 && roofTakes(r, w, i, 1)) {
        roofOrder(s, h, i, 1);
        return true;
      }
    }
  }
  return false;
};

/* ---------- chapter 4 ---------- */

/** Strikes only what is already in reach (a step would call the work off, or leave the lee). */
const stillStrike = (s: BattleState, h: Hero): void => {
  const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range + 200, (e) => targetable(s, e)), h.x, h.y);
  h.target = t ? t.id : 0;
  h.forced = false;
};

/** The rock's lee nearest a point (a cell's middle), or null. */
const leeNear = (s: BattleState, lee: number[], x: number, y: number): { x: number; y: number } | null => {
  let best: { x: number; y: number } | null = null;
  let bd = 0x7fffffff;
  for (const c of lee) {
    if (!heroWalkable(s, c)) continue;
    const p = cellCenter(s.map.radius, c);
    const d = dist2(x, y, p.x, p.y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

/** The loose slope: with Kel up the trail; a front's shadow showing, into a rock's lee (who knows). */
const looseBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.slide;
  const w = s.senda?.slide;
  if (!r?.loose || !w) return;
  const kel = npc(s, 'kel');
  if (knows && slideShadow(s, r, w)) {
    const p = leeNear(s, r.lee, h.x, h.y);
    if (p && !(cellAtWorld(s, h.x, h.y) === cellAtWorld(s, p.x, p.y))) {
      go(s, h, p.x, p.y, 200);
      h.target = 0;
      return;
    }
    stillStrike(s, h);
    return;
  }
  const g = w.giant ? s.enemies.find((e) => e.id === w.giant && e.hp > 0) : undefined;
  if (g && w.awake && kel && withinRange(g.x, g.y, kel.x, kel.y, 4500)) {
    strike(s, h, g);
    return;
  }
  const sleeping = (e: Enemy): boolean => e.id === w.giant && !w.awake;
  if (kel && guard(s, h, kel.x, kel.y, 3000, (e) => !sleeping(e))) return;
  if (guard(s, h, h.x, h.y, 2600, (e) => !sleeping(e))) return;
  h.target = 0;
  const next = r.loose.way[imin(r.loose.way.length - 1, w.way + 1)];
  if (!kel || !next) return;
  // Between Kel and where he goes next (who doesn't know stands out on the trail beside him, the open side).
  let x = knows ? (kel.x + next.x) >> 1 : kel.x + (kel.x < next.x ? 1100 : -1100);
  const y = knows ? ((kel.y + next.y) >> 1) - 400 : kel.y - 200;
  // Who knows keeps off the giant asleep beside the trail (who doesn't walks where the trail takes it).
  if (knows && g && !w.awake && withinRange(x, y, g.x, g.y, r.loose.wake + 900)) x = kel.x + (kel.x > g.x ? 700 : -700);
  go(s, h, x, y);
};

/** The ice cave: the room's sentinels, from behind (who knows) or from the front; on to the next door, then the hall. */
const caveBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.cave;
  const st = s.senda?.cave;
  if (!r || !st) return;
  // The awake sentinels, nearest first.
  const awake: Enemy[] = [];
  st.ids.forEach((ids, i) => {
    if (!st.awake[i]) return;
    for (const id of ids) {
      const e = s.enemies.find((x) => x.id === id && x.hp > 0);
      if (e) awake.push(e);
    }
  });
  const t = nearestTo(awake, h.x, h.y);
  if (t) {
    if (knows) {
      // In its guard's arc: round to its back (or a side when the back is ice).
      const to = iatan2(h.y - t.y, h.x - t.x);
      let d = (to - t.facing) % 1024;
      if (d > 512) d -= 1024;
      if (d < -512) d += 1024;
      if (d <= r.arc + 40 && d >= -r.arc - 40) {
        for (const off of [512, 512 - 200, 512 + 200, 256, 768]) {
          const a = t.facing + off;
          const x = t.x + idiv(cosA(a) * 850, 10000);
          const y = t.y + idiv(sinA(a) * 850, 10000);
          const c = cellAtWorld(s, x, y);
          if (c >= 0 && heroWalkable(s, c)) {
            orderMove(s, h, x, y);
            h.target = 0;
            return;
          }
        }
      }
    }
    strike(s, h, t);
    return;
  }
  const kel = npc(s, 'kel');
  if (kel && guard(s, h, kel.x, kel.y, 2500)) return;
  h.target = 0;
  // Between rooms, a breath first (the next room's sentinels sleep until the hero walks in).
  if (h.hp * 20 < h.stats.maxHp * 19) return;
  const room = st.cleared.findIndex((c) => !c);
  const at = room < 0 ? r.hall : (r.rooms[room] as { at: { x: number; y: number } }).at;
  go(s, h, at.x, at.y);
};

/** The slope the large wedges stand on (the bot reads its answer, as one who worked it out). */
const SLOPE = new WeakMap<BattleState, ReturnType<typeof avalancheLayout>>();
const slopeOf = (s: BattleState): ReturnType<typeof avalancheLayout> => {
  let L = SLOPE.get(s);
  if (!L) {
    L = avalancheLayout(s.seed);
    SLOPE.set(s, L);
  }
  return L;
};

/** The large wedges: the four of the answer (or four down the middle), then out of the snow's way at the mouth. */
const lanesBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.slide;
  const w = s.senda?.slide;
  const L = r?.lanes;
  if (!r || !w || !L) return;
  const rocks = slideRocks(r);
  const want = knows ? slopeOf(s).solution : laneObvious(L.rows.map((x) => x.rocks));
  // A rock's shadow on the hero: out of it (unless the wedge is all but in).
  if (!(w.at >= 0 && w.held >= L.stand - 8))
    for (const x of w.stones) {
      if (!withinRange(h.x, h.y, x.x, x.y, L.stones.r + 250)) continue;
      const dx = h.x - x.x;
      const dy = h.y - x.y;
      const d = isqrt(dx * dx + dy * dy) || 1;
      const k = L.stones.r + 700;
      const tx = x.x + idiv(dx * k, d) + (d < 50 ? k : 0);
      const ty = x.y + idiv(dy * k, d);
      const c = cellAtWorld(s, tx, ty);
      orderMove(s, h, c >= 0 && heroWalkable(s, c) ? tx : x.x - idiv(dx * k, d), c >= 0 && heroWalkable(s, c) ? ty : x.y + k);
      h.target = 0;
      w.at = -1;
      return;
    }
  if (w.at >= 0) {
    stillStrike(s, h);
    return;
  }
  const soon = slideShadow(s, r, w) && w.next - s.tick < 5 * 12;
  if (!soon) {
    // A wedge where the answer wants none: out. One the answer wants: in (nearest first).
    for (let i = 0; i < rocks.length; i++)
      if (w.shut[i] === 1 && !want.includes(i)) {
        slideOrder(s, h, i);
        return;
      }
    let best = -1;
    let bd = 0x7fffffff;
    for (const i of want) {
      if (w.shut[i] === 1 || w.hand <= 0) continue;
      const k = rocks[i] as { x: number; y: number };
      const d = dist2(h.x, h.y, k.x, k.y);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    if (best >= 0) {
      // What is on the hero first, when it's already at it (not the giant: it walks on).
      if (guard(s, h, h.x, h.y, 1300, (e) => e.id !== w.giant)) return;
      slideOrder(s, h, best);
      return;
    }
  }
  // The work done (or the fall coming): where no snow comes down, fighting what comes.
  const safe = { x: L.mouth.x, y: L.mouth.y - 900 };
  if (snowAt(r, w, safe.x, safe.y) > 0) safe.x = L.x0 + L.laneW * 3 + (L.laneW >> 1);
  if (guard(s, h, safe.x, safe.y, 2200, (e) => e.id !== w.giant)) return;
  h.target = 0;
  go(s, h, safe.x, safe.y);
};


/* ---------- chapter 5: the Pass of the Rift ---------- */

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0 && !n.away);

/** The ledge of the wind: by Harl, striking cubs; who knows takes a niche when the wind warns. */
const cragBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.crag;
  const w = s.senda?.crag;
  if (!r || !w) return;
  const harl = person(s, 'harlOld');
  if (knows && cragWarn(s, r, w)) {
    let best: { x: number; y: number } | null = null;
    let bd = 0x7fffffff;
    for (const c of r.niches) {
      const p = cellCenter(s.map.radius, c);
      const d = dist2(h.x, h.y, p.x, p.y);
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
    if (best) {
      if (!guard(s, h, best.x, best.y, 900)) h.target = 0;
      go(s, h, best.x, best.y, 120);
      return;
    }
  }
  if (!harl) return;
  const wenn = person(s, 'wenn');
  if (guard(s, h, harl.x, harl.y, 3200)) return;
  if (wenn && guard(s, h, wenn.x, wenn.y, 2600)) return;
  if (guard(s, h, h.x, h.y, 1800)) return;
  h.target = 0;
  // Just behind Harl on the ledge's middle (between him and Wenn).
  const p = r.way[imax(0, w.way - 2)] as { x: number; y: number };
  go(s, h, p.x, p.y);
};

/** The camp: cleared by Harl; then the line, as the pages say (who knows) or in the order they fell (who doesn't). */
const lineBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.line;
  const w = s.senda?.line;
  if (!r || !w) return;
  if (w.phase < 2) {
    const harl = person(s, 'harlOld');
    const at = harl ?? h;
    if (guard(s, h, at.x, at.y, 6000)) return;
    const t = nearestTo(enemiesNear(s, h.x, h.y, 14000, (e) => targetable(s, e)), h.x, h.y);
    if (t) strike(s, h, t);
    return;
  }
  if (w.phase === 3) return;
  // A cub on its way to the line: first.
  for (const c of w.cubs) {
    const e = findEnemy(s, c.id);
    if (e && e.hp > 0 && withinRange(e.x, e.y, h.x, h.y, 5000)) return strike(s, h, e);
  }
  h.target = 0;
  if (w.at >= 0) {
    // Sent, and pulled off by a cub: back to it.
    const p = w.at >= LINE_TAKE ? (w.pile[w.at - LINE_TAKE] === 2 ? r.ruy : r.stretcher) : r.slots[w.at];
    if (p && !h.moving && !withinRange(h.x, h.y, p.x, p.y, r.reach)) lineOrder(s, h, w.at);
    return;
  }
  const free = [0, 1, 2, 3, 4, 6, 7, 8, 9, 10];
  const want = (who: number): number => (knows ? LINE_ANSWER[LINE_NAMES[who] as (typeof LINE_NAMES)[number]] - 1 : who === LINE_NAMES.indexOf('ruy') ? 4 : (free.filter((x) => x !== 4)[who - (who > LINE_NAMES.indexOf('ruy') ? 1 : 0)] as number));
  if (w.hand >= 0) return lineOrder(s, h, want(w.hand));
  // A shield in a slot not its own: take it out; else the next on the stretcher (Ruy's by his stone).
  for (let i = 0; i < w.on.length; i++) {
    const who = w.on[i] as number;
    if (who >= 0 && want(who) !== i) return lineOrder(s, h, i);
  }
  for (let who = 0; who < LINE_NAMES.length; who++) if (w.pile[who]) return lineOrder(s, h, LINE_TAKE + who);
  if (!knows) lineOrder(s, h, LINE_SETTLE);
};

/** The basket each number wants: who knows, the number less one in four stones, as close as can be to what is in it. */
const beamWant = (basket: number[], total: number, slots: number): number[] | null => {
  let best: number[] | null = null;
  let bd = 99;
  for (const way of beamWays([1, 2, 3], slots, total)) {
    const left = basket.filter((x) => x > 0);
    let keep = 0;
    for (const v of way) {
      const k = left.indexOf(v);
      if (k >= 0) {
        left.splice(k, 1);
        keep++;
      }
    }
    const d = way.length - keep + left.length;
    if (d < bd) {
      bd = d;
      best = way;
    }
  }
  return best;
};

const beamBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.beam;
  const w = s.senda?.beam;
  if (!r || !w) return;
  // What is on Harl first (he has to stand), then the cubs on the basket, then what comes at the hero.
  const harl0 = person(s, 'harlOld');
  if (harl0 && guard(s, h, harl0.x, harl0.y, 1400)) return;
  for (const c of w.cubs) {
    const e = findEnemy(s, c.id);
    if (e && e.hp > 0 && withinRange(e.x, e.y, r.foot.x, r.foot.y, 3500)) return strike(s, h, e);
  }
  const harl = person(s, 'harlOld');
  if (guard(s, h, harl?.x ?? h.x, harl?.y ?? h.y, 2200)) return;
  if (guard(s, h, h.x, h.y, 1500)) return;
  h.target = 0;
  if (w.at >= 0) {
    const p = w.at >= BEAM_PLACE ? r.foot : r.stones[w.at];
    if (p && !h.moving && !withinRange(h.x, h.y, p.x, p.y, r.reach)) beamOrder(s, h, w.at);
    return;
  }
  if (w.fallen || (w.calm && s.tick >= w.next)) return;
  const load = beamLoad(w);
  if (!knows) {
    // Only adding, when it's light.
    if (load >= w.num - 1) return;
    const k = w.basket.indexOf(0);
    if (k < 0) return;
    const v = imin(3, w.num - 1 - load);
    if (w.hand !== v) return beamOrder(s, h, v - 1);
    return beamOrder(s, h, BEAM_PLACE + k);
  }
  const want = beamWant(w.basket, w.num - 1, r.slots);
  if (!want) return;
  const left = want.slice();
  const extra: number[] = [];
  w.basket.forEach((v, i) => {
    if (!v) return;
    const k = left.indexOf(v);
    if (k >= 0) left.splice(k, 1);
    else extra.push(i);
  });
  if (!left.length && !extra.length && !w.hand) return;
  if (w.hand) {
    const k = left.indexOf(w.hand);
    if (k >= 0) {
      // In an empty place, or in place of one not wanted (never at the number or over).
      const slot = extra.length ? (extra[0] as number) : w.basket.indexOf(0);
      if (slot >= 0 && load - (w.basket[slot] as number) + w.hand < w.num) return beamOrder(s, h, BEAM_PLACE + slot);
    }
    const need = left[0];
    if (need) return beamOrder(s, h, need - 1);
    // Nothing more wanted: an empty place takes it out of the hands only if it fits.
    return;
  }
  // The stone wanted first, then swapped for the one not wanted (taking that one out first leaves the basket light,
  // and the beam gives while the hero walks to the heaps).
  const need = left[0];
  if (need) return beamOrder(s, h, need - 1);
  if (extra.length) beamOrder(s, h, BEAM_PLACE + (extra[0] as number));
};

const stormBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.storm;
  const w = s.senda?.storm;
  if (!r || !w) return;
  const boss = findEnemy(s, w.boss);
  // Out of a sweep's way (unless the wedge it is driving goes in before it lands).
  const finishing = knows && (w.at === 0 || w.at === 1) && w.held > 0 && r.stand - w.held < w.sweep - s.tick - 2;
  if (w.sweep >= 0 && !finishing && withinRange(h.x, h.y, w.sx, w.sy, r.sweep.r + 200)) {
    const dx = h.x - w.sx;
    const away = dx >= 0 ? 1 : -1;
    const tx = w.sx + away * (r.sweep.r + 500);
    go(s, h, Math.abs(tx) > 3800 ? w.sx - away * (r.sweep.r + 500) : tx, h.y, 200);
    h.target = 0;
    return;
  }
  // Off the next charge's path when it is about to come (unless a wedge in its footprint goes in first).
  const nx0 = w.prints.length ? (w.prints[0] as { x: number }).x + 380 : w.x;
  const planting = (w.at === 0 || w.at === 1) && w.held > 0 && r.stand - w.held < w.next - s.tick - 1;
  if (knows && w.wedged < 0 && !planting && w.next - s.tick < 36 && Math.abs(h.x - nx0) < 1500 && h.y > w.y - 600 && h.y < w.y + r.step + 900) {
    const side = Math.abs(nx0 + 2400) > 3800 ? -1 : Math.abs(nx0 - 2400) > 3800 ? 1 : h.x >= nx0 ? 1 : -1;
    go(s, h, nx0 + side * 2400, h.y, 200);
    h.target = 0;
    return;
  }
  if (knows) {
    if (w.at >= 0) {
      const at = w.at;
      const p = stormSpot(s, at);
      if (!p) w.at = -1;
      const there = !p || withinRange(h.x, h.y, p.x, p.y, at >= STORM_ROCK && at < STORM_STONE ? 1150 : r.reach + (at >= STORM_LIFT ? 400 : 0));
      // On the way: no blows (they'd pull it off the way); there, it strikes what it can without moving.
      if (!there) {
        h.target = 0;
        if (!h.moving) stormOrder(s, h, at);
      } else stillStrike(s, h);
      return;
    }
    // Harl's shield, then its slot.
    if (w.shield === 1) return stormOrder(s, h, STORM_SHIELD);
    if (w.shield === 2) return stormOrder(s, h, STORM_SLOT);
    // The breath with the beam ready: the stone.
    if (w.breath > 0 && r.ready && !w.stone) return stormOrder(s, h, STORM_STONE);
    // The avalanche called: the right two rocks.
    if (w.fall >= 0 && !w.fell) {
      const all = stormRocks(r);
      const right = (s.rules?.senda?.storm?.rocks ? solveSlide(r) : []) as number[];
      for (let k = 0; k < all.length; k++) if (w.shut[k] === 1 && !right.includes(k)) return stormOrder(s, h, STORM_ROCK + k);
      for (const k of right) if (w.shut[k] !== 1 && w.hand > 0) return stormOrder(s, h, STORM_ROCK + k);
    }
    // A footprint before the charge, with a wedge in hand: once it has come near enough the line that the pile is a
    // short way off (far up the pass, the trips to the pile are too long: let it come, fight it off its path).
    const left = w.next - s.tick;
    const near = w.left <= 7 * 866;
    if (w.wedged < 0 && w.hand > 0 && w.prints.length && left > 0 && near) {
      let k = 0;
      if (dist2(h.x, h.y, w.prints[1]!.x, w.prints[1]!.y) < dist2(h.x, h.y, w.prints[0]!.x, w.prints[0]!.y)) k = 1;
      return stormOrder(s, h, k);
    }
    // Wedges from the pile.
    if (w.hand < r.hand && w.pile > 0 && (w.hand === 0 || withinRange(h.x, h.y, r.pileAt.x, r.pileAt.y, 3500))) {
      go(s, h, r.pileAt.x, r.pileAt.y);
      return stillStrike(s, h);
    }
    if (!w.banner) return stormOrder(s, h, STORM_BANNER);
    const st = s.senda;
    const down = st ? st.npcs.findIndex((n) => !n.away && n.hp <= 0 && (n.id === 'wenn' || n.id === 'dalmo')) : -1;
    if (down >= 0) return stormOrder(s, h, STORM_LIFT + down);
    // The cubs near the line, then the giant when it's open to blows.
    for (const c of w.cubs) {
      const e = findEnemy(s, c.id);
      if (e && e.hp > 0 && e.y > r.lineY - 3500) return strike(s, h, e);
    }
  }
  for (const c of w.cubs) {
    const e = findEnemy(s, c.id);
    if (e && e.hp > 0 && withinRange(e.x, e.y, h.x, h.y, 1600)) return strike(s, h, e);
  }
  if (boss && boss.hp > 0) {
    // Who knows strikes it between charges and stands off its next charge's path.
    const nx = w.prints.length ? ((w.prints[0] as { x: number }).x + 380) : w.x;
    if (knows && w.next - s.tick < 30 && Math.abs(h.x - nx) < 1700 && h.y > w.y - 400) {
      go(s, h, nx + (h.x >= nx ? 2300 : -2300), h.y, 200);
      h.target = 0;
      return;
    }
    strike(s, h, boss);
  }
};

/** The avalanche's right pair (the bot that knows reads it, as a player would by the grain). */
const solveSlide = (r: NonNullable<NonNullable<BattleState['rules']>['senda']>['storm'] & object): number[] => {
  for (let m = 0; m < 8; m++) {
    const shut = [m & 1, (m >> 1) & 1, (m >> 2) & 1];
    if (shut[0]! + shut[1]! + shut[2]! !== 2) continue;
    if (passClear(passFlow(r.rocks, r.snow, shut))) return [0, 1, 2].filter((i) => shut[i] === 1);
  }
  return [];
};

type CanalR = NonNullable<NonNullable<NonNullable<BattleState['rules']>['senda']>['canal']>;

/** Whether the water down canal `e` reaches a pool with some setting of the stones past it (the canals followed by eye). */
const toPool = (r: CanalR, e: number, depth = 0): boolean => {
  const edge = r.edges[e];
  if (!edge || depth > 8) return false;
  if (edge.to === 'pool') return true;
  if (edge.to === 'sea') return false;
  const st = r.stones[edge.n];
  if (!st) return false;
  if (st.mouth >= 0) return true;
  return toPool(r, st.out[0], depth + 1) || toPool(r, st.out[1], depth + 1);
};

/** The way on at a fork: the canal out of it whose water can reach a pool (the first, if both). */
const goodWay = (r: CanalR, i: number): number => {
  const st = r.stones[i];
  if (!st) return 0;
  return toPool(r, st.out[0]) ? 0 : 1;
};

/** Whether the wave running is past stone `i` (its front already down one of its canals, or further on). */
const past = (r: CanalR, w: NonNullable<NonNullable<BattleState['senda']>['canal']>, i: number): boolean => {
  const f = w.front;
  if (!f) return false;
  // Walk from the inlet by the stones as they are: the stone is past if the front's canal comes after it.
  let e = r.inlet;
  for (let k = 0; k < 10; k++) {
    if (e === f.e) return false;
    const edge = r.edges[e];
    if (!edge || edge.to !== 'stone') return true;
    if (edge.n === i) return f.e !== e;
    const st = r.stones[edge.n];
    if (!st) return true;
    e = st.mouth >= 0 ? st.out[0] : (st.out[w.pos[edge.n] ?? 0] as number);
  }
  return true;
};

/** Off a burnt line (the plough's): to the nearest cell round it that isn't. */
const offBurn = (s: BattleState, h: Hero): boolean => {
  const c = cellAtWorld(s, h.x, h.y);
  if (!canalBurnt(s, c)) return false;
  const nb = geo(s.map.radius).nb;
  for (let d = 0; d < 6; d++) {
    const n = nb[c * 6 + d] as number;
    if (n >= 0 && heroWalkable(s, n) && !canalBurnt(s, n)) {
      const p = cellCenter(s.map.radius, n);
      orderMove(s, h, p.x, p.y);
      return true;
    }
  }
  return false;
};

/** Out of the Arado's way while its blades are down (its three lines marked ahead of it): aside, across its way. */
const dodgePlow = (s: BattleState, h: Hero): boolean => {
  const w = s.senda?.canal;
  const r = s.rules?.senda?.canal;
  if (!w || !r?.plow || w.plow.mode === 0 || !w.plow.id) return false;
  const e = s.enemies.find((x) => x.id === w.plow.id && x.hp > 0);
  if (!e) return false;
  const p = w.plow;
  // Where the hero is along its way and across it (sim units): ahead within its run and within a hex and a half aside.
  const rx = h.x - e.x;
  const ry = h.y - e.y;
  const along = idiv(rx * p.dx + ry * p.dy, 1000);
  const side = idiv(rx * p.dy - ry * p.dx, 1000);
  const run = r.plow.len * 1000 + 600;
  if (along < -600 || along > run || Math.abs(side) > 1700) return false;
  const k = side >= 0 ? 1 : -1;
  const tx = h.x + idiv(p.dy * 2300 * k, 1000);
  const ty = h.y - idiv(p.dx * 2300 * k, 1000);
  h.target = 0;
  go(s, h, tx, ty, 200);
  return true;
};

/** Sends the hero to a stone (once: its order stands while it walks and works). */
const toStone = (s: BattleState, h: Hero, i: number): void => {
  const w = s.senda?.canal;
  if (w && w.job !== i) canalOrder(s, h, i);
  h.target = 0;
};

/** Keeps the crías and the Arado off Marit (within `r` of her), else what reaches the hero; false if nothing is. */
const keepMarit = (s: BattleState, h: Hero, r = 2800): boolean => {
  const m = npc(s, 'marit');
  if (m && m.hp > 0 && guard(s, h, m.x, m.y, r)) return true;
  return guard(s, h, h.x, h.y, 2200);
};

/** The canals: the stones set (who knows: by following the canals; who doesn't: one more tried after each wave lost). */
const canalBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.canal;
  const w = s.senda?.canal;
  if (!r || !w) return;
  if (knows && dodgePlow(s, h)) {
    w.job = -1;
    return;
  }
  if (w.job >= 0) return;
  if (offBurn(s, h)) return;
  if (r.kind === 'small') {
    if (knows) {
      for (let i = 0; i < r.stones.length; i++) {
        if (w.pos[i] === goodWay(r, i) || past(r, w, i)) continue;
        return toStone(s, h, i);
      }
    } else {
      // A wave lost: try another stone (by the waves lost so far), before the next wave comes.
      const want = BLIND_TRY.get(s) ?? -1;
      if (w.spilt > (BLIND_SEEN.get(s) ?? 0)) {
        BLIND_SEEN.set(s, w.spilt);
        BLIND_TRY.set(s, (s.seed + w.spilt * 5) % r.stones.length);
      }
      if (want >= 0 && !w.front) {
        BLIND_TRY.set(s, -1);
        return toStone(s, h, want);
      }
    }
  } else {
    // The test. The inlet's fork the way on; the pools' fork and the mouth by the plan.
    if (w.pos[0] !== goodWay(r, 0) && !past(r, w, 0)) return toStone(s, h, 0);
    const lowP = r.pools.findIndex((p) => !p.high);
    const highP = r.pools.findIndex((p) => p.high);
    const mouth = (r.pools[highP] as CanalR['pools'][number]).mouth;
    const climb = r.edges.findIndex((e) => e.climb && e.to === 'stone');
    const fork = r.stones.findIndex((st) => st.mouth < 0 && (st.out[0] === climb || st.out[1] === climb));
    const upPos = (r.stones[fork] as CanalR['stones'][number]).out[0] === climb ? 0 : 1;
    const big = w.front ? w.front.big : w.big;
    const highFull = (w.level[highP] as number) >= r.fill;
    const lowFull = (w.level[lowP] as number) >= r.fill;
    let up: boolean;
    let shut: boolean;
    if (knows) {
      up = big && !highFull && w.pos[mouth] === 0;
      shut = highFull;
    } else {
      up = big && lowFull && !highFull;
      shut = false;
    }
    // The mouth first once the high pool is full (the sea drops soon after the last great wave).
    if (shut !== (w.pos[mouth] === 1) && !(w.front && !past(r, w, mouth))) return toStone(s, h, mouth);
    if (!past(r, w, fork) && (w.pos[fork] === upPos) !== up) return toStone(s, h, fork);
  }
  // The test: the Arado and the crías drawn off to where the plough can't reach a canal (who knows), fought there.
  if (knows && r.plow && w.plow.id && s.enemies.some((e) => e.id === w.plow.id && e.hp > 0)) {
    const b = baitOf(s, r);
    // Never onto its hot lines: while it stands on one, the crías first, and it waits.
    const plow = s.enemies.find((e) => e.id === w.plow.id) as Enemy;
    const hot = canalBurnt(s, cellAtWorld(s, plow.x, plow.y)) || w.plow.mode !== 0;
    if (guard(s, h, b.x, b.y, 2600, (e) => !hot || e.id !== plow.id)) return;
    h.target = 0;
    go(s, h, b.x, b.y);
    return;
  }
  if (keepMarit(s, h)) return;
  h.target = 0;
  const m = npc(s, 'marit');
  if (m) go(s, h, m.x + 700, m.y - 400);
};

/**
 * Where to fight the Arado on the test's shore: the glass farthest from every canal (and off Marit, three hexes at
 * least), so its plough runs away from them. Worked out once a fight.
 */
const BAIT = new WeakMap<BattleState, { x: number; y: number }>();
const baitOf = (s: BattleState, r: CanalR): { x: number; y: number } => {
  const hit = BAIT.get(s);
  if (hit) return hit;
  let best = r.keep;
  let bestScore = -1;
  const R = s.map.radius;
  const cells = (R * 2 + 1) * (R * 2 + 1);
  for (let c = 0; c < cells; c++) {
    if (!heroWalkable(s, c) || r.canal.includes(c) || r.surf.includes(c)) continue;
    const p = cellCenter(R, c);
    if (withinRange(p.x, p.y, r.keep.x, r.keep.y, 3200)) continue;
    let near = 0x7fffffff;
    for (const k of r.canal) {
      const q = cellCenter(R, k);
      const d = Math.abs(q.x - p.x) + Math.abs(q.y - p.y);
      if (d < near) near = d;
    }
    // Inland (the Burn's side) and away from the canals.
    const score = near * 2 + p.y;
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }
  BAIT.set(s, best);
  return best;
};

/** The blind canal bot's tries: the waves lost it has seen, and the stone it means to try next. */
const BLIND_SEEN = new WeakMap<BattleState, number>();
const BLIND_TRY = new WeakMap<BattleState, number>();

/** The second altar: what comes off the altar and Marit; who knows stands by Marit when the cone is marked on her. */
const kindleBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.kindle;
  const w = s.senda?.kindle;
  const m = npc(s, 'marit');
  if (!r || !w) return;
  if (knows && w.marked && m && m.hp > 0) {
    const d = s.enemies.find((e) => e.id === w.drake);
    if (d && inCone(d.x, d.y, w.aim, r.drake.range, r.drake.arc, m.x, m.y)) {
      h.target = 0;
      go(s, h, m.x + 450, m.y, 150);
      return;
    }
  }
  const altar = npc(s, 'altar');
  if (altar && altar.hp > 0 && guard(s, h, altar.x, altar.y, 2600)) return;
  if (m && m.hp > 0 && guard(s, h, m.x, m.y, 2600)) return;
  if (guard(s, h, h.x, h.y, 2600)) return;
  h.target = 0;
  if (altar) go(s, h, altar.x + 800, altar.y - 300);
};


type SaltR = NonNullable<NonNullable<NonNullable<BattleState['rules']>['senda']>['salt']>;

/** The fog: the point of the dykes' way it walks to next, by fight. */
const ROUTE = new WeakMap<BattleState, number>();
/** The ways a stalker has been seen on (its tracks), by fight. */
const TRACKS = new WeakMap<BattleState, number[]>();
/** The gates' plan for the tide at hand, by fight: the gates in order, and the tide it is for. */
const PLAN = new WeakMap<BattleState, { tide: number; gates: number[] }>();

/**
 * The gates' short way from here, as on paper: from the pools as they will be once the water already running has run,
 * with the gates still free. (A gate turned before the plan was made, by the player's own tap or by a turn begun as the
 * sea came back, is never put again in an order that no longer holds.)
 */
const sluicePlan = (r: SaltR, w: NonNullable<NonNullable<BattleState['senda']>['salt']>): number[] => {
  const G = r.gates ?? [];
  const lv = w.level.slice();
  for (const f of w.flows) {
    const g = G[f.g] as (typeof G)[number];
    const [na, nb] = sluiceFlow(g.down, lv[g.a] as number, lv[g.b] as number, r.cap);
    lv[g.a] = na;
    lv[g.b] = nb;
  }
  const free: number[] = [];
  for (let g = 0; g < G.length; g++) if ((w.locked[g] as number) === 0) free.push(g);
  const best = sluiceSolve(free.map((g) => G[g] as (typeof G)[number]), lv, r.altar)[0] ?? [];
  return best.map((k) => free[k] as number);
};

const saltBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.salt;
  const w = s.senda?.salt;
  const m = npc(s, 'marit');
  if (!r || !w) return;
  if (r.kind === 'fog') {
    // Marit kept close (she limps after, the way it went); what it sees by her, struck on the way.
    // (Who knows doesn't chase off the dyke: it strikes only what is in reach and on salt.)
    if (guard(s, h, h.x, h.y, knows ? 1100 : 2000, (e) => !knows || saltPoolOf(s, e.x, e.y) < 0)) return;
    h.target = 0;
    if (m && !withinRange(h.x, h.y, m.x, m.y, 2300)) return go(s, h, h.x, h.y);
    // Who knows: down the dykes by the layout's way. Who doesn't: straight across.
    if (!knows) return go(s, h, r.door.x, r.door.y + 300);
    const route = saltLayout(s.seed).route;
    const at = ROUTE.get(s) ?? 0;
    const p = route[at] as { x: number; y: number };
    if (withinRange(h.x, h.y, p.x, p.y, 450) && at < route.length - 1) ROUTE.set(s, at + 1);
    // A step at a time along the dyke (a long walk takes any of the hex grid's equal ways, across the pools).
    const dx = p.x - h.x;
    const dy = p.y - h.y;
    const step = 1300;
    const far = Math.abs(dx) + Math.abs(dy);
    const k = far > step ? step : far;
    const tx = Math.abs(dx) >= Math.abs(dy) ? h.x + (dx < 0 ? -k : k) : p.x;
    const ty = Math.abs(dx) >= Math.abs(dy) ? p.y : h.y + (dy < 0 ? -k : k);
    return go(s, h, tx, ty, 150);
  }
  if (r.kind === 'sluice' && r.gates) {
    // What strikes it at the wheel first (a blow starts the turn over).
    if (guard(s, h, h.x, h.y, 1000)) {
      w.job = -1;
      return;
    }
    if (knows) {
      const p = PLAN.get(s);
      if (!p || p.tide !== w.returns) PLAN.set(s, { tide: w.returns, gates: sluicePlan(r, w) });
      const plan = (PLAN.get(s) as { gates: number[] }).gates;
      // What blocks the way to the next gate first, then the gate (a turn begun for the tide before, dropped).
      const next = plan.find((g) => w.locked[g] === 0);
      if (w.job >= 0 && w.job === next) return;
      if (next !== undefined && w.dried < 0) {
        saltOrder(s, h, next);
        return;
      }
    } else if (w.job >= 0) return;
    else if (w.dried < 0) {
      // The obvious: the altar's gate down, then the rest in a row.
      const order = [1, 0, 2, 3, 4, 5];
      const next = order.find((g) => w.locked[g] === 0);
      if (next !== undefined) {
        saltOrder(s, h, next);
        return;
      }
    }
    if (m && m.hp > 0 && guard(s, h, m.x, m.y, 2600)) return;
    if (guard(s, h, h.x, h.y, 2600)) return;
    h.target = 0;
    return go(s, h, r.door.x + 900, r.door.y + 300);
  }
  // The test.
  const H = r.hold;
  const ways = r.ways;
  if (!H || !ways) return;
  const A = r.pools[r.altar] as { x: number; y: number };
  const old = w.old.id ? s.enemies.find((e) => e.id === w.old.id && e.hp > 0) : undefined;
  let watch: { x: number; y: number } | null = null;
  if (knows) {
    // The tracks: when each way was last seen trodden (on its dry pool or a dyke).
    const seen = TRACKS.get(s) ?? ways.map(() => -1);
    TRACKS.set(s, seen);
    for (let i = 0; i < w.onWay.length; i++) {
      const e = s.enemies.find((x) => x.id === w.onWay[i]);
      if (e && e.hp > 0 && saltSeen(s, e)) seen[w.way[i] as number] = s.tick;
    }
    const recent = ways.map((_, i) => i).filter((i) => (seen[i] as number) >= 0 && s.tick - (seen[i] as number) < sec(40)).sort((a, b) => (seen[b] as number) - (seen[a] as number));
    if (recent.length) {
      const way = ways[recent[0] as number] as NonNullable<SaltR['ways']>[number];
      watch = way.pts[way.choke] as { x: number; y: number };
    }
    // The old stalker on its mark: stand on it.
    if (old && w.old.mode === 1) {
      h.target = 0;
      go(s, h, w.old.x, w.old.y, 150);
      return;
    }
    if (s.tick >= w.ready && !w.throwing && m && m.hp > 0) {
      // By Marit, unseen: salt it there.
      if (old && w.old.mode === 2 && !saltSeen(s, old)) saltThrow(s, cellAtWorld(s, old.x, old.y));
      else {
        for (const wi of recent) {
          const way = ways[wi] as NonNullable<SaltR['ways']>[number];
          const c = way.pts[way.choke] as { x: number; y: number };
          if (w.circles.some((k) => withinRange(k.x, k.y, c.x, c.y, 600) && k.until > s.tick + 160)) continue;
          saltThrow(s, cellAtWorld(s, c.x, c.y));
          break;
        }
      }
    }
  } else if (s.tick >= w.ready && !w.throwing) saltThrow(s, cellAtWorld(s, A.x + 300, A.y));
  const altar = s.senda?.npcs.find((n, i) => r && s.rules?.senda?.npcs[i]?.prop);
  if (altar && altar.hp > 0 && guard(s, h, altar.x, altar.y, 3200)) return;
  if (m && m.hp > 0 && guard(s, h, m.x, m.y, 2600)) return;
  if (guard(s, h, h.x, h.y, 2200)) return;
  h.target = 0;
  if (watch) go(s, h, (watch.x + A.x) >> 1, (watch.y + A.y) >> 1);
  else go(s, h, A.x + 500, A.y + 300);
};

type ArchR = NonNullable<NonNullable<NonNullable<BattleState['rules']>['senda']>['arch']>;

/** The last band the bot swept, by fight (who doesn't know sweeps each band once as it comes up). */
const SWEPT = new WeakMap<BattleState, number>();

/** The bridge's ends: nightmares at the lower end (y > 0) and at the upper (y < 0), out of the middle. */
const archBusy = (s: BattleState, w: NonNullable<NonNullable<BattleState['senda']>['arch']>): [number, number] => {
  let low = 0;
  let high = 0;
  for (const e of s.enemies) {
    if (e.hp <= 0 || w.mammoth.includes(e.id)) continue;
    if (e.y > 1300) low++;
    else if (e.y < -1300) high++;
  }
  return [low, high];
};

const archBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.arch as ArchR | undefined;
  const w = s.senda?.arch;
  if (!r || !w) return;
  if (w.job >= 0) return;
  const L = archLayout(s.seed);
  const T = s.waveT;
  const C = r.cistern;
  const M = r.mammoth;
  // The gutters right before anything but a mammoth's head down (the rain moves while it waits at the gargoyle).
  const headDown = w.mode.some((x, k) => x === 1 && (w.mammoth[k] as number) > 0);
  if (C && !headDown && (knows || r.kind === 'vigil')) {
    for (const j of [1 - w.rain, w.rain])
      if (w.open[j] !== (j === w.rain ? 1 : 0)) {
        archOrder(s, h, j);
        return;
      }
  }
  // The night's mammoths first: at the gargoyle as one nears the top, the lever as its head goes down.
  if (M && C) {
    for (let k = 0; k < 2; k++) {
      const id = w.mammoth[k] as number;
      if (id <= 0) continue;
      const m = s.enemies.find((e) => e.id === id && e.hp > 0);
      if (!m) continue;
      const lever = r.levers[2 + k] as { x: number; y: number };
      if (w.mode[k] === 1) {
        if (w.level >= r.sweep.drop) archOrder(s, h, 2 + k);
        return;
      }
      if (w.mode[k] === 0 && Math.abs(m.y) < r.head + 900) {
        h.target = 0;
        if (guard(s, h, lever.x, lever.y, 900)) return;
        return go(s, h, lever.x, lever.y, 150);
      }
      // Climbing, far down: who knows lets it come (the keepers' ends first); who doesn't goes down to fight it.
      if (w.mode[k] === 0 && !knows) {
        if (guard(s, h, m.x, m.y, 1400)) return;
        return go(s, h, m.x, m.y + (k === 0 ? -900 : 900));
      }
    }
  }
  if (C) {
    // The gutters: the rain's open, the dry one shut (who doesn't know opens both).
    const want = knows || r.kind === 'vigil' ? [w.rain === 0 ? 1 : 0, w.rain === 1 ? 1 : 0] : [1, 1];
    // (The dry one shut first: both open, the cistern runs out.)
    for (const j of [1 - w.rain, w.rain])
      if (w.open[j] !== want[j]) {
        archOrder(s, h, j);
        return;
      }
    // What stands on the rain's gutter, first.
    if (w.blocked[w.rain]) {
      const g = r.gutters[w.rain] as ArchR['gutters'][number];
      if (guard(s, h, (g.x0 + g.x1) >> 1, (g.y0 + g.y1) >> 1, 1800)) return;
    }
    if (r.kind === 'vigil' && w.level >= r.sweep.drop) {
      // A band on the stairs near the top: who doesn't know sweeps it; who knows only with the cistern full, no
      // mammoth out, and time to fill it again before the next one comes up.
      const next = M ? M.at.reduce((lo, a, k) => imin(lo, (w.mammoth[k] as number) === 0 ? a + M.climb : 1_000_000), 1_000_000) : 1_000_000;
      const out = w.mammoth.some((x) => x > 0);
      for (let k = 0; k < 2; k++) {
        const on = s.enemies.filter((e) => e.hp > 0 && !w.mammoth.includes(e.id) && (k === 0 ? e.y > r.head : e.y < -r.head) && Math.abs(e.y) < r.head + 2600);
        if (!on.length) continue;
        const ok = knows ? w.level >= C.cap && !out && next - T > sec(40) && on.some((e) => e.kind === 'sentinel' && !archSideOn(s, e)) : (SWEPT.get(s) ?? -1) < w.band;
        if (ok) {
          if (!knows) SWEPT.set(s, w.band);
          archOrder(s, h, 2 + k);
          return;
        }
      }
    }
  }
  const [low, high] = archBusy(s, w);
  const mid = L.middle[w.rain === 0 ? 0 : 1] as { x: number; y: number };
  if (!knows && !C) {
    // Who doesn't know keeps by Marit.
    const m = s.senda?.npcs.find((n) => n.id === 'marit');
    const at = m ?? (L.keeps.marit as { x: number; y: number });
    if (guard(s, h, at.x, at.y, 2600)) return;
    h.target = 0;
    return go(s, h, at.x, at.y - 600);
  }
  if (low && high && (low >= 3 || high >= 3)) {
    // Both ends, one of them full: beside the altar, the aura over both; strike what comes within reach.
    if (guard(s, h, mid.x, mid.y, 1500)) return;
    h.target = 0;
    return go(s, h, mid.x, mid.y, 150);
  }
  if (low || high) {
    // One end (or both, light): the busier one; Marit keeps the other.
    const y = low > high ? 2600 : -2600;
    if (guard(s, h, 0, y, 3200)) return;
    h.target = 0;
    return go(s, h, 0, y > 0 ? 2200 : -2200);
  }
  if (guard(s, h, mid.x, mid.y, 2000)) return;
  h.target = 0;
  go(s, h, mid.x, mid.y, 150);
};

type MonR = NonNullable<NonNullable<NonNullable<BattleState['rules']>['senda']>['monastery']>;
type MonW = NonNullable<NonNullable<BattleState['senda']>['monastery']>;

/** The doors the bot that doesn't know has tried (by fight), and the groups it has seen come out. */
const TRIED = new WeakMap<BattleState, { next: number; outs: number }>();

/** Where the prints at a crossing came from: −1 none since `since`; else the arm's spot (2 west north, 3 west south, 4 east north, 5 east south). */
const printArm = (w: MonW, since: number): number => {
  for (let i = 0; i + 4 < w.prints.length; i += 5) {
    const spot = w.prints[i] as number;
    if (spot > 1 || (w.prints[i + 4] as number) < since) continue;
    const a = (((w.prints[i + 3] as number) % 1024) + 1024) % 1024;
    // Walking down the board (south, +y): from the north arm; up it: from the south.
    if (a >= 160 && a <= 352) return spot === 0 ? 2 : 4;
    if (a >= 672 && a <= 864) return spot === 0 ? 3 : 5;
  }
  return -1;
};

/** Whether prints show on a spot since a tick. */
const printed = (w: MonW, spot: number, since: number): boolean => {
  for (let i = 0; i + 4 < w.prints.length; i += 5) if ((w.prints[i] as number) === spot && (w.prints[i + 4] as number) >= since) return true;
  return false;
};

const monBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.monastery as MonR | undefined;
  const w = s.senda?.monastery;
  if (!r || !w || w.job >= 0) return;
  const L = monasteryLayout(s.seed);
  if (r.porch) {
    const edda = npc(s, 'edda');
    if (!edda) return;
    if (knows) {
      // At her side: what comes at the door, struck from beside her.
      if (guard(s, h, edda.x, edda.y, 2400)) {
        if (!monCovered(s, h)) go(s, h, edda.x + 700, edda.y + 200);
        return;
      }
      h.target = 0;
      return go(s, h, edda.x + 700, edda.y + 200, 150);
    }
    // Down the gorge to meet them.
    if (guard(s, h, 0, 6500, 5000)) return;
    h.target = 0;
    return go(s, h, 0, 6000);
  }
  if (r.cells) {
    const C = r.cells;
    const seen = (e: Enemy): boolean => monSeen(s, e);
    const doors = C.spots.length;
    const sack = C.spots.length + C.doors.length;
    if (knows) {
      // What the salt shows near, struck first.
      if (guard(s, h, h.x, h.y, 2200, seen)) return;
      for (const j of [0, 1])
        if (!w.salted[j]) {
          if (w.salt <= 0) return monOrder(s, h, sack);
          return monOrder(s, h, j);
        }
      const arm = printArm(w, 0);
      if (arm < 0) {
        h.target = 0;
        return go(s, h, 0, -5000, 400);
      }
      if (!w.salted[arm]) {
        if (w.salt <= 0) return monOrder(s, h, sack);
        return monOrder(s, h, arm);
      }
      // The arm salted: a group through the crossing since, and through the arm or not.
      const since = w.salted[arm] as number;
      const far = printed(w, arm, since);
      // (A group already past the arm's spot as the salt fell reaches the crossing a few seconds later: not counted.)
      const passed = printArm(w, since + sec(5)) === arm;
      if (far || passed) {
        const cell = arm === 2 ? (far ? 0 : 1) : arm === 3 ? (far ? 3 : 2) : arm === 4 ? (far ? 4 : 5) : far ? 7 : 6;
        if (w.shut !== cell) return monOrder(s, h, doors + cell);
      }
      const p = C.spots[arm] as { x: number; y: number };
      h.target = 0;
      return go(s, h, p.x, p.y + (p.y < -5000 ? 1400 : -1400), 400);
    }
    // Who doesn't know: salt where they come (the chapel's door), keep the altar, try a door each time it goes out.
    if (!w.salted[6]) {
      if (w.salt <= 0) return monOrder(s, h, sack);
      return monOrder(s, h, 6);
    }
    const t = TRIED.get(s) ?? { next: 0, outs: 0 };
    TRIED.set(s, t);
    // (A door tried each time a group comes out, as long as the last one didn't stop them.)
    if (w.band > t.outs && t.next < 8 && s.waveT >= C.first + (w.band - 1) * C.every + sec(10)) {
      t.outs = w.band;
      const d = t.next++;
      return monOrder(s, h, doors + d);
    }
    if (guard(s, h, 0, -6800, 2600, seen)) return;
    h.target = 0;
    return go(s, h, 600, -6400, 300);
  }
  const K = r.cloister;
  if (!K) return;
  const marit = L.keeps.maritCloister as { x: number; y: number };
  const want = (g: number, open: boolean): boolean => {
    if (!!w.open[g] === open) return false;
    monOrder(s, h, g);
    return true;
  };
  // The kitchen shut before its second snuff (the plate says when the next one puts the altar out), with a mark to spare.
  if (w.open[1] && (w.out[1] as number) >= K.snuffAt + K.snuffEvery - 2 && want(1, false)) return;
  const boss = w.bossId > 0 ? s.enemies.find((e) => e.id === w.bossId && e.hp > 0) : undefined;
  if (knows) {
    if (!w.out[0] && !w.open[0] && w.level > K.bare && want(0, true)) return;
    if (!w.out[1] && !w.open[1] && w.level > K.bare && want(1, true)) return;
    // The ravine once the siege has gone quiet (nothing of it standing), or when there's no other way left.
    // (Quiet: no band for a while longer than they come, as a player sees it.)
    const last = w.band > 0 ? (K.siege.at[w.band - 1] as number) : 1_000_000;
    const quiet = w.band > 0 && s.waveT - last > K.siege.every + sec(5) && !s.enemies.some((e) => e.hp > 0 && e.id !== w.bossId);
    if (!w.lit && w.level > K.bare && !w.open[2] && (quiet || s.waveT > K.boss.late - sec(20)) && (w.out[0] as number) >= K.orchards && want(2, true)) return;
    if (w.lit && w.open[2] && want(2, false)) return;
    // The great sentinel up: met at the chapel's door, Tadeo behind.
    if (boss) {
      if (guard(s, h, 0, -6200, 3200, (e) => e.id === boss.id || withinRange(e.x, e.y, h.x, h.y, 1200))) return;
      h.target = 0;
      return go(s, h, 0, -5900, 300);
    }
    if (guard(s, h, marit.x, marit.y, 3800)) return;
    h.target = 0;
    return go(s, h, marit.x + 800, marit.y + 300, 300);
  }
  if (!w.lit && w.level > K.bare && !w.open[2] && want(2, true)) return;
  if (!w.out[0] && !w.open[0] && w.level > K.bare && want(0, true)) return;
  if (!w.out[1] && !w.open[1] && w.level > K.bare && want(1, true)) return;
  const t = nearestTo(enemiesNear(s, h.x, h.y, 14000, (e) => targetable(s, e)), h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
  go(s, h, marit.x + 800, marit.y + 300, 300);
};

/**
 * And its chapter 5, the seventh altar:
 *
 * - the rubble (`seventh`, rubble): who knows strikes the bone singer first, then what goes for Marit, and clears the
 *   heaps nearest her in between; who doesn't clears the heaps one after another and strikes only what reaches it.
 * - the stair (`descent`): the crypt's own bot, Marit behind.
 * - the well (`seventh`, well, the test): who knows works out the marks to take (down to three, the spring counted) and
 *   turns the gates near Marit that make them (the ravine and the yard), then waits at the lip by Marit striking the
 *   squires while the water shows them; who doesn't turns the ravine three times (it drains fastest) and waits the same.
 * - La Que Huyó (`seventh`, fled): who knows stands on each line marked for Marit (hers, the thick one, when two), strikes
 *   her when she shows, shuts the gates she strikes open, salts the runnels by where she was last seen, and keeps by the
 *   altar between runs (when the hero swears); who doesn't goes after her where she shows, and waits by Marit.
 */
const SEV_PLAN = new WeakMap<BattleState, number[]>();
const sevBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.seventh;
  const w = s.senda?.seventh;
  if (!r || !w) return;
  const marit = person(s, 'marit');
  const seen = (e: Enemy): boolean => sevSeen(s, e);
  if (r.rubble) {
    const R = r.rubble;
    if (knows) {
      const singer = s.enemies.find((e) => e.hp > 0 && e.kind === 'bonesinger' && targetable(s, e));
      if (singer) return strike(s, h, singer);
      if (marit && guard(s, h, marit.x, marit.y, 2400)) return;
    } else if (guard(s, h, h.x, h.y, 1300)) return;
    // The heap nearest Marit (who knows) or the next in line (who doesn't), worked standing still beside it.
    let k = -1;
    let bd = 0x7fffffff;
    for (let i = 0; i < R.piles.length; i++) {
      if ((w.cleared[i] as number) >= R.stand) continue;
      const p = R.piles[i] as { x: number; y: number };
      const d = knows && marit ? dist2(p.x, p.y, marit.x, marit.y) : i;
      if (d < bd) {
        bd = d;
        k = i;
      }
    }
    if (k < 0) return;
    const p = R.piles[k] as { x: number; y: number };
    if (withinRange(h.x, h.y, p.x, p.y, R.reach - 200)) {
      // At it: strike only what is in reach (no step away).
      const t = nearestTo(enemiesNear(s, h.x, h.y, 1100, (e) => targetable(s, e)), h.x, h.y);
      if (t) {
        h.target = t.id;
        h.forced = false;
      }
      return;
    }
    h.target = 0;
    return go(s, h, p.x, p.y + 500, 200);
  }
  const W = r.well;
  if (!W) return;
  if (r.kind === 'well') {
    if (w.job >= 0) return;
    // The plan: the marks to take, worked out once from the start (who knows), or the ravine three times.
    let plan = SEV_PLAN.get(s);
    if (!plan) {
      plan = knows ? [3, 3, 0] : [3, 3, 3];
      SEV_PLAN.set(s, plan);
    }
    const turned = SEV_TURNED.get(s) ?? 0;
    // The marks still to come out of the gates already turned.
    let pending = 0;
    for (let g = 0; g < 4; g++) pending += (w.open[g] as number) * (W.take[g] as number) - (w.took[g] as number);
    if (turned < plan.length) {
      const g = plan[turned] as number;
      // (Who knows checks the count first: the spring may have come; the last mark it needs is the yard's one.)
      const left = w.level - pending - plan.slice(turned).reduce((a, x) => a + (W.take[x] as number), 0);
      if (!knows || left >= W.ring) {
        SEV_TURNED.set(s, turned + 1);
        return sevOrder(s, h, SEV_GATE + g);
      }
      SEV_TURNED.set(s, plan.length);
    } else if (!pending && w.level > W.bare) {
      // The spring came more than once: one more (the yard's mark, or the ravine again for who doesn't know).
      return sevOrder(s, h, SEV_GATE + (knows ? (w.level - W.bare >= 3 && w.level - 3 >= W.ring ? 3 : 0) : 3));
    }
    // At the lip by Marit: the squires while the water shows them.
    if (marit) {
      if (guard(s, h, marit.x, marit.y - 1200, 2600, seen)) return;
      h.target = 0;
      return go(s, h, marit.x + 300, marit.y - 1400, 300);
    }
    return;
  }
  const F = r.fled;
  if (!F) return;
  const e = findEnemy(s, w.boss);
  if (!e || e.hp <= 0) {
    h.target = 0;
    return;
  }
  const line = w.act === 1 && w.line.length === 4 ? w.line : null;
  if (knows) {
    // (Hidden, at a gate's work: a line far off is left to Marit, the gate shut first.)
    const busy = w.phase === 1 && w.job >= 0 && w.held > 0 && !!line && !withinRange(h.x, h.y, line[2] as number, line[3] as number, 3200);
    // (Marit can take a few runs: with the hero low and her still whole, this one is hers.)
    const spare = !!marit && marit.hp * 10000 >= marit.maxHp * 5000 && h.hp * 10000 < h.stats.maxHp * 4000;
    if (line && !busy && !spare) {
      // On her line, a little before Marit (hers when two: the thick one).
      if (sevOnLine(F, h, line)) {
        h.target = withinRange(h.x, h.y, e.x, e.y, 1300) && targetable(s, e) ? e.id : 0;
        return;
      }
      w.job = -1;
      const dx = (line[2] as number) - (line[0] as number);
      const dy = (line[3] as number) - (line[1] as number);
      return go(s, h, (line[2] as number) - idiv(dx, 6), (line[3] as number) - idiv(dy, 6), 120);
    }
    // The squires first, as the water shows them on their way to Marit; then her, when she shows (hidden, wherever).
    if (guard(s, h, marit?.x ?? 0, marit?.y ?? 0, 4200, (x) => x.id !== e.id && seen(x))) {
      w.job = -1;
      return;
    }
    if (targetable(s, e) && withinRange(e.x, e.y, h.x, h.y, w.phase === 1 ? 7000 : 3600)) {
      w.job = -1;
      return strike(s, h, e);
    }
    if (w.job >= 0) return;
    // Hidden: salt on the floor round Marit (she shows coming), between runs.
    if (w.phase === 1 && marit) {
      let k = -1;
      let bd = 0x7fffffff;
      for (let i = 0; i < F.runnels.length; i++) {
        if (w.salt[i]) continue;
        const d = segDist2(marit.x, marit.y, F.runnels[i] as number[]).d2;
        if (d < bd) {
          bd = d;
          k = i;
        }
      }
      if (k >= 0 && bd < 3600 * 3600 && s.tick < w.next - sec(4)) return sevOrder(s, h, SEV_RUNNEL + k);
    }
    // Between runs: between the altar and Marit (by the altar, if the hero swears).
    h.target = 0;
    const mx = marit?.x ?? 0;
    const my = marit?.y ?? 700;
    return go(s, h, idiv(mx, 2) + 300, idiv(my, 2) + 500, 300);
  }
  // Who doesn't know: after her wherever she shows, and to where she was last seen when she doesn't.
  if (line && w.job >= 0) w.job = -1;
  if (targetable(s, e)) return strike(s, h, e);
  if (guard(s, h, h.x, h.y, 2400, seen)) return;
  h.target = 0;
  const last = w.marks.length >= 3 ? { x: w.marks[w.marks.length - 3] as number, y: w.marks[w.marks.length - 2] as number } : { x: w.tx, y: w.ty };
  go(s, h, last.x, last.y, 400);
};
const SEV_TURNED = new WeakMap<BattleState, number>();

// ---------- the artificer's chapter 1 (docs/sendas/artifice.md): Nadd's workshop ----------

/** Out of a stone's shadow on the ledge (along the way, the shorter side); true if it had to. */
const offShadow = (s: BattleState, h: Hero): boolean => {
  const r = s.rules?.senda?.cornice;
  const w = s.senda?.cornice;
  if (!r || !w) return false;
  for (const st of w.stones) {
    if (!withinRange(st.x, st.y, h.x, h.y, r.stone.r + 250)) continue;
    // Up or down the ledge, whichever row out of it is nearer.
    let best: { x: number; y: number } | null = null;
    let bd = 0x7fffffff;
    for (const p of r.way) {
      if (withinRange(st.x, st.y, p.x, p.y, r.stone.r + 450) || w.stones.some((o) => withinRange(o.x, o.y, p.x, p.y, r.stone.r + 300))) continue;
      const d = dist2(p.x, p.y, h.x, h.y);
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
    if (best) {
      h.target = 0;
      go(s, h, best.x, best.y, 150);
      return true;
    }
  }
  return false;
};

/**
 * The goat path. Who knows keeps at Grillo's back (he only goes on with the hero near), steps out of every stone's
 * shadow, and goes up to each slinger's nest first (they throw at Grillo); then the crows on Grillo. Who doesn't
 * follows Grillo and fights whatever comes nearest, shadows or not.
 */
const corniceBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.cornice;
  const w = s.senda?.cornice;
  const g = npc(s, 'grillo');
  if (!r || !w || !g) return;
  if (knows) {
    if (offShadow(s, h)) return;
    // What is on Grillo first (the crows pecking at him), then the slingers in their nests, the nearest to him first.
    if (guard(s, h, g.x, g.y, 1500)) return;
    const sl = w.slingers.map((x) => findEnemy(s, x.id)).filter((e): e is Enemy => !!e && e.hp > 0);
    const t = nearestTo(sl, g.x, g.y);
    if (t && withinRange(t.x, t.y, g.x, g.y, 9000)) return strike(s, h, t);
    if (guard(s, h, g.x, g.y, 2600)) return;
  } else {
    const t = nearestTo(enemiesNear(s, h.x, h.y, 5000, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
  }
  h.target = 0;
  // At Grillo's back: a little behind him down the ledge.
  const i = imax(0, w.crawl >= 0 ? w.crawl - 1 : w.way - 2);
  const p = r.way[i] as { x: number; y: number };
  if (!withinRange(h.x, h.y, g.x, g.y, r.wait - 900)) go(s, h, p.x, p.y);
};

/** Where to stand to strike a golem in its back (behind it, `d` off), or null if that's not floor. */
const behindOf = (s: BattleState, e: Enemy, d: number): { x: number; y: number } | null => {
  const a = e.facing + 512;
  const x = e.x + idiv(cosA(a) * d, 10000);
  const y = e.y + idiv(sinA(a) * d, 10000);
  return heroWalkable(s, cellAtWorld(s, x, y)) ? { x, y } : null;
};

/** Out of a golem's raised arm: aside, across where it will strike. True if it had to. */
const dodgeArm = (s: BattleState, h: Hero): boolean => {
  const gr = s.rules?.senda?.golem;
  const list = s.senda?.golem?.list;
  if (!gr || !list) return false;
  for (const g of list) {
    if (g.wind < 0 || g.mode !== 1) continue;
    const e = findEnemy(s, g.id);
    if (!e) continue;
    const B = g.boss && gr.boss ? gr.boss : null;
    const reach = (B ? B.reach : gr.reach) + 500;
    const arc = (B ? B.arc : gr.arc) + 60;
    if (!inArc(e.x, e.y, g.aim, reach, arc, h.x, h.y)) continue;
    // Round to its back, the side the hero already leans to.
    const side = turnOf(g.aim, iatan2(h.y - e.y, h.x - e.x)) >= 0 ? 1 : -1;
    for (const k of [side, -side]) {
      const a = g.aim + k * 420;
      const x = e.x + idiv(cosA(a) * 1500, 10000);
      const y = e.y + idiv(sinA(a) * 1500, 10000);
      if (!heroWalkable(s, cellAtWorld(s, x, y))) continue;
      h.target = 0;
      go(s, h, x, y, 150);
      return true;
    }
  }
  return false;
};

/** Out of a golem's spinning arms: straight away from it, past their reach. True if it had to. */
const dodgeSpin = (s: BattleState, h: Hero): boolean => {
  const gr = s.rules?.senda?.golem;
  for (const g of s.senda?.golem?.list ?? []) {
    if (g.spin < 0 || !gr) continue;
    const e = findEnemy(s, g.id);
    if (!e || !withinRange(e.x, e.y, h.x, h.y, gr.spin.r + 300)) continue;
    const dx = h.x - e.x;
    const dy = h.y - e.y;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    for (const turn of [0, 160, -160, 320, -320]) {
      const a = iatan2(dy, dx) + turn;
      const far = gr.spin.r + 900;
      const x = e.x + idiv(cosA(a) * far, 10000);
      const y = e.y + idiv(sinA(a) * far, 10000);
      if (!heroWalkable(s, cellAtWorld(s, x, y))) continue;
      h.target = 0;
      go(s, h, x, y, 150);
      void d;
      return true;
    }
  }
  return false;
};

/** An awake golem near a point (within `r`), the nearest; null if none. */
const golemNear = (s: BattleState, x: number, y: number, r: number): { g: GolemUnit; e: Enemy } | null => {
  let best: { g: GolemUnit; e: Enemy } | null = null;
  let bd = 0x7fffffff;
  for (const g of s.senda?.golem?.list ?? []) {
    if (g.mode !== 1 && g.mode !== 2) continue;
    const e = findEnemy(s, g.id);
    if (!e || e.hp <= 0 || !withinRange(e.x, e.y, x, y, r)) continue;
    const d = dist2(e.x, e.y, x, y);
    if (d < bd) {
      bd = d;
      best = { g, e };
    }
  }
  return best;
};

/**
 * A golem fought: who knows gets round to its back and strikes from there (its boiler), and strikes it whole while it's
 * out; who doesn't strikes it from wherever it stands.
 */
const fightGolem = (s: BattleState, h: Hero, g: GolemUnit, e: Enemy, knows: boolean): void => {
  if (!knows || g.mode === 2) return strike(s, h, e);
  const gr = s.rules?.senda?.golem;
  const behind = turnOf(e.facing + 512, iatan2(h.y - e.y, h.x - e.x));
  const back = (gr?.back ?? 200) - 40;
  if (behind <= back && behind >= -back && withinRange(h.x, h.y, e.x, e.y, h.stats.range + 200)) return strike(s, h, e);
  // Round it: a point behind it, near enough to strike from.
  const d = imin(h.stats.range - 300, 2200);
  const p = behindOf(s, e, imax(900, d)) ?? behindOf(s, e, 900);
  if (!p) return strike(s, h, e);
  h.target = 0;
  go(s, h, p.x, p.y, 200);
};

/** The sleeping workshop: down hall by hall to the lift; what woke, fought (from behind, who knows). */
const workshopBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.workshop;
  const w = s.senda?.workshop;
  if (!r || !w) return;
  if (knows && (dodgeSpin(s, h) || dodgeArm(s, h))) return;
  const near = golemNear(s, h.x, h.y, 5200);
  if (near) return fightGolem(s, h, near.g, near.e, knows);
  if (guard(s, h, h.x, h.y, 2600)) return;
  h.target = 0;
  // Dunna lagging: wait for her.
  const d = npc(s, 'dunna');
  if (d && d.hp > 0 && !withinRange(d.x, d.y, h.x, h.y, 3200)) return;
  // On to the stair of the hall the hero is in (its grille up), or the lift.
  const hall = r.halls[w.hall];
  const next = w.hall < r.halls.length - 1 && hall?.stair.length ? cellCenter(s.map.radius, hall.stair[0] as number) : r.lift;
  const below = w.hall < r.halls.length - 1 ? (r.halls[w.hall + 1]?.at ?? r.lift) : r.lift;
  // Past the stair: into the next hall.
  if (withinRange(h.x, h.y, next.x, next.y, 700) && next !== r.lift) return go(s, h, next.x, below.y);
  go(s, h, next.x, next.y);
};

/** The blind gear bot's tries: the train it means to set (teeth on each axle), and how many it gave up on. */
const GEAR_TRY = new WeakMap<BattleState, { want: number[]; n: number }>();

/** A random way from the wheel to the drum through one or two middles, and random pieces on it (the blind bot's try). */
const blindTry = (s: BattleState): number[] => {
  const r = s.rules?.senda?.gears as NonNullable<NonNullable<NonNullable<BattleState['rules']>['senda']>['gears']>;
  const n = r.axles.length;
  const k = GEAR_TRY.get(s)?.n ?? 0;
  // (Its own luck, from the fight's seed and the tries so far: it doesn't move the fight's.)
  let x = (s.seed ^ (k * 2654435761)) >>> 0;
  const rnd = (m: number): number => {
    x = (Math.imul(x ^ (x >>> 15), 2246822507) ^ k) >>> 0;
    return x % m;
  };
  for (let tries = 0; tries < 30; tries++) {
    const mids = rnd(2) + 1;
    const way = [0];
    for (let i = 0; i < mids; i++) {
      const at = way[way.length - 1] as number;
      const opts = (r.adj[at] as number[]).filter((j) => j !== 1 && !way.includes(j));
      if (!opts.length) break;
      way.push(opts[rnd(opts.length)] as number);
    }
    if (way.length !== mids + 1 || !(r.adj[way[way.length - 1] as number] as number[]).includes(1)) continue;
    way.push(1);
    const pool = [...r.pieces];
    const set = new Array<number>(n).fill(0);
    for (const a of way) {
      if (!pool.length) break;
      set[a] = pool.splice(rnd(pool.length), 1)[0] as number;
    }
    return set;
  }
  return new Array<number>(n).fill(0);
};

/**
 * One step toward a setting of the axles (`want`: teeth on each; 0 empty): what is wrong on an axle taken off and laid
 * down, then each axle in the train's order given what it asks. False when it is set.
 */
const gearStep = (s: BattleState, h: Hero, want: number[], order: number[]): boolean => {
  const r = s.rules?.senda?.gears;
  const w = s.senda?.gears;
  if (!r || !w) return false;
  if (w.job >= 0) return true;
  const A = r.axles.length;
  const teethOn = (slot: number): number => {
    const k = gearAt(w, slot);
    return k >= 0 ? (r.pieces[k] as number) : 0;
  };
  const send = (slot: number): boolean => {
    gearOrder(s, h, slot);
    return true;
  };
  // In hand: where it goes (an axle asking for its teeth and empty), else down on an empty lying spot.
  if (w.hand >= 0) {
    const teeth = r.pieces[w.hand] as number;
    for (const a of order) if ((want[a] as number) === teeth && teethOn(a) === 0) return send(a);
    for (let k = 0; k < r.pieces.length; k++) if (gearAt(w, A + k) < 0) return send(A + k);
    return false;
  }
  // A gear on an axle that shouldn't have it: off.
  for (let a = 0; a < A; a++) {
    const t = teethOn(a);
    if (t && t !== (want[a] as number)) return send(a);
  }
  // The next axle of the train that wants its gear: fetch one with those teeth (lying first).
  for (const a of order) {
    const need = want[a] as number;
    if (!need || teethOn(a) === need) continue;
    for (let k = 0; k < r.pieces.length; k++) if (r.pieces[k] === need && w.on[k] !== undefined && (w.on[k] as number) >= A) return send(w.on[k] as number);
    for (let b = 0; b < A; b++) if (b !== a && teethOn(b) === need && (want[b] as number) !== need) return send(b);
  }
  return false;
};

/**
 * The wheel and the belt. Who knows works the train out (the wheel's and the drum's gears by the speed, two middles so
 * the drum turns the other way) and sets it axle by axle from the wheel, fighting the golems that come near (from
 * behind) and those at Dunna; then the Overseer, from behind. Who doesn't tries trains at random from the wheel to the
 * drum (one middle or two, any pieces), one after another, and fights from where it stands.
 */
const gearsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.gears;
  const w = s.senda?.gears;
  if (!r || !w) return;
  // (A gear half taken or put is seen through, as a person would: the arm up over it or not.)
  if (knows && !(w.job >= 0 && w.held > 0) && (dodgeSpin(s, h) || dodgeArm(s, h))) {
    w.job = -1;
    return;
  }
  // The Overseer, once awake.
  if (w.belt >= 0) {
    const boss = golemOf(s, w.boss);
    const e = boss ? findEnemy(s, boss.id) : undefined;
    // Its golems first when they're on the hero or Dunna.
    const near = golemNear(s, h.x, h.y, 2200);
    if (near && !near.g.boss) return fightGolem(s, h, near.g, near.e, knows);
    if (boss && e && e.hp > 0 && boss.mode !== 3) return fightGolem(s, h, boss, e, knows);
    if (guard(s, h, h.x, h.y, 5000)) return;
    return;
  }
  const d = npc(s, 'dunna');
  // The golems on the hero, or on Dunna.
  const mine = golemNear(s, h.x, h.y, knows ? 2400 : 3000);
  if (mine) {
    w.job = -1;
    return fightGolem(s, h, mine.g, mine.e, knows);
  }
  if (knows && d && d.hp > 0) {
    const at = golemNear(s, d.x, d.y, 2600);
    if (at) {
      w.job = -1;
      return fightGolem(s, h, at.g, at.e, knows);
    }
  }
  if (guard(s, h, h.x, h.y, 1800)) return;
  let want: number[];
  if (knows) {
    want = gearSolve({ axles: r.axles.map(() => ({ q: 0, r: 0 })), adj: r.adj, wheel: r.wheel, want: r.want, pieces: r.pieces })[0] ?? r.axles.map(() => 0);
  } else {
    const t = GEAR_TRY.get(s) ?? { want: blindTry(s), n: 0 };
    GEAR_TRY.set(s, t);
    want = t.want;
  }
  // The order: from the wheel along the train (each axle next to one already in it), then the drum.
  const order = [0];
  for (let k = 0; k < want.length && order.length < want.filter((x) => x > 0).length; k++)
    for (let a = 2; a < want.length; a++) if ((want[a] as number) > 0 && !order.includes(a) && order.some((o) => (r.adj[o] as number[]).includes(a))) order.push(a);
  if ((want[1] as number) > 0) order.push(1);
  if (gearStep(s, h, want, order)) return;
  // Set: wait for the belt (who doesn't, having set a wrong train, tries another).
  if (!knows && w.solved < 0) {
    const t = GEAR_TRY.get(s) as { want: number[]; n: number };
    t.n++;
    t.want = blindTry(s);
    return;
  }
  h.target = 0;
  if (d) go(s, h, d.x + 900, d.y - 500);
};

export const sendaBot = (s: BattleState, h: Hero): void => {
  if (h.downTicks > 0 || h.stun > 0) return;
  const r = s.rules?.senda;
  if (!r) return;
  const knows = r.bot !== 'blind';
  // The Long Hunt's acts (the barbarian's chapter 1, ai/caza-bot.ts).
  if (cazaBot(s, h, knows)) return;
  // The burnt library's acts (the mage's chapter 1, ai/mago-bot.ts).
  if (magoBot(s, h, knows)) return;
  if (r.wedge) return wedgeBot(s, h, knows);
  if (r.hooks) return hooksBot(s, h, knows);
  if (r.gust) return gustBot(s, h, knows);
  if (r.weight) return weightBot(s, h, knows);
  if (r.slope) return slopeBot(s, h, knows);
  if (r.roof) return roofBot(s, h, knows);
  if (r.slide?.loose) return looseBot(s, h, knows);
  if (r.slide?.lanes) return lanesBot(s, h, knows);
  if (r.cave) return caveBot(s, h, knows);
  if (r.crag) return cragBot(s, h, knows);
  if (r.line) return lineBot(s, h, knows);
  if (r.beam) return beamBot(s, h, knows);
  if (r.storm) return stormBot(s, h, knows);
  if (r.canal) return canalBot(s, h, knows);
  if (r.kindle) return kindleBot(s, h, knows);
  if (r.salt) return saltBot(s, h, knows);
  if (r.arch) return archBot(s, h, knows);
  if (r.monastery) return monBot(s, h, knows);
  if (r.seventh) return sevBot(s, h, knows);
  if (r.cornice) return corniceBot(s, h, knows);
  if (r.workshop) return workshopBot(s, h, knows);
  if (r.gears) return gearsBot(s, h, knows);
  if (r.crane) return craneBot(s, h, knows);
  if (r.gondola) return gondolaBot(s, h, knows);
  if (r.gearbox) return gearboxBot(s, h, knows);
  if (r.vent) return ventBot(s, h, knows);
  if (r.ember) return emberBot(s, h, knows);
  if (r.cams) return camsBot(s, h, knows);
  if (r.struts) return strutsBot(s, h, knows);
  if (r.carts) return cartsBot(s, h, knows);
  if (r.drills) return drillsBot(s, h, knows);
  if (r.knees) return kneesBot(s, h, knows);
  if (r.torno) return tornoBot(s, h, knows);
  if (r.shell) return shellBot(s, h, knows);
  if (r.plates) return platesBot(s, h, knows);
  if (r.litter && clericBot(s, h, knows)) return;
  if (r.druid) return druidBot(s, h, knows);
  if (r.summer) return summerBot(s, h, knows);
  if (r.autumn) return autumnBot(s, h, knows);
  if (r.winter) return winterBot(s, h, knows);
  if (r.deep) return deepBot(s, h, knows);
  if (r.cairns) return cairnsBot(s, h, knows);
  if (r.echo) return echoBot(s, h, knows);
  if (r.fallen) return fallenBot(s, h, knows);
  if (r.cliff) return cliffBot(s, h, knows);
  if (s.mode === 'dungeon') return dungeonBot(s, h);
  if (r.hunt) return fieldBot(s, h, knows);
  const t = nearestTo(enemiesNear(s, h.x, h.y, 14000, (e) => targetable(s, e)), h.x, h.y);
  if (t) strike(s, h, t);
};

/** Where in the ledge the hero is (for the bot's own checks in tests). */
export const ledgeCell = (s: BattleState, h: Hero): number => cellAtWorld(s, h.x, h.y);

// ---------- the artificer's chapter 2 (docs/sendas/artifice.md): the cable ----------

/** The cable's flyers sitting on perches of a kind (their enemies), the struck-down left out. */
const perched = (s: BattleState, kind: number): Enemy[] => {
  const r = s.rules?.senda?.garg;
  const out: Enemy[] = [];
  for (const g of s.senda?.garg?.list ?? []) {
    if (g.mode !== 1 || !r || r.perches[g.perch]?.kind !== kind) continue;
    const e = findEnemy(s, g.id);
    if (e && e.hp > 0) out.push(e);
  }
  return out;
};

/** The flyers going for someone (not perched, not La Picotera) within `r` of a point. */
const hunters = (s: BattleState, x: number, y: number, r: number): Enemy[] => {
  const out: Enemy[] = [];
  for (const g of s.senda?.garg?.list ?? []) {
    if (g.mode !== 2 && g.mode !== 3) continue;
    const e = findEnemy(s, g.id);
    if (e && e.hp > 0 && withinRange(e.x, e.y, x, y, r)) out.push(e);
  }
  return out;
};

/** The walkable cell centre nearest a point among those within `r` of it (null: none). */
const standNear = (s: BattleState, h: Hero, x: number, y: number, r: number): { x: number; y: number } | null => {
  let best: { x: number; y: number } | null = null;
  let bd = 0x7fffffff;
  const R = s.map.radius;
  for (let c = 0, n = geo(R).q.length; c < n; c++) {
    if (!heroWalkable(s, c)) continue;
    const p = cellCenter(R, c);
    if (!withinRange(p.x, p.y, x, y, r)) continue;
    const d = dist2(p.x, p.y, h.x, h.y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

/** Strikes `e`, walking first to a cell within reach of it when out of it. */
const reachFor = (s: BattleState, h: Hero, e: Enemy): void => {
  if (withinRange(h.x, h.y, e.x, e.y, h.stats.range - 200)) return strike(s, h, e);
  const p = standNear(s, h, e.x, e.y, h.stats.range - 400);
  if (!p) return strike(s, h, e);
  h.target = 0;
  go(s, h, p.x, p.y, 200);
};

/**
 * The crane. Who knows keeps the arm clear: what sits on it first (walking out on the scaffold for the far perches,
 * but out of the swaying piece's sweep), then what goes for Dunna, then what goes for it. Who doesn't fights what is
 * nearest, by Dunna, and never goes out on the scaffold.
 */
const craneBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.crane;
  const w = s.senda?.crane;
  const d = npc(s, 'dunna');
  if (!r || !w) return;
  if (!knows) {
    const at = d && d.hp > 0 ? d : h;
    const t = nearestTo(enemiesNear(s, at.x, at.y, 3600, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    if (d) go(s, h, d.x - 600, d.y + 700);
    return;
  }
  // Out of the sweep while the piece sways.
  if (w.sway >= 0 && withinRange(h.x, h.y, r.swing.at.x, r.swing.at.y, r.swing.r + 300)) {
    h.target = 0;
    return go(s, h, r.swing.at.x - r.swing.r - 900, r.swing.at.y + 300, 150);
  }
  const arm = perched(s, 0);
  // The farthest out first (the hardest to reach once it sways).
  arm.sort((a, b) => b.x - a.x || a.id - b.id);
  const t = arm[0];
  if (t && !(w.sway >= 0 && withinRange(t.x, t.y, r.swing.at.x, r.swing.at.y, r.swing.r + 1200) && !withinRange(h.x, h.y, t.x, t.y, h.stats.range - 200))) return reachFor(s, h, t);
  if (d && d.hp > 0) {
    const on = nearestTo(hunters(s, d.x, d.y, 2200), d.x, d.y);
    if (on) return reachFor(s, h, on);
  }
  const mine = nearestTo(hunters(s, h.x, h.y, 2600), h.x, h.y);
  if (mine) return strike(s, h, mine);
  if (t) return reachFor(s, h, t);
  h.target = 0;
  go(s, h, 1000, -500);
};

/**
 * The car. Who knows stands by a post when a tilt is warned (and holds there till it rights), and keeps the cable clear
 * (what sits on it first, from the deck's end nearest it), then the roof, then what flies at it. Who doesn't fights
 * what is nearest and pays the tilts no mind.
 */
const gondolaBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.gondola;
  const w = s.senda?.gondola;
  if (!r || !w) return;
  if (!knows) {
    const t = nearestTo(enemiesNear(s, h.x, h.y, 6000, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return;
  }
  const nearestHold = (x: number, y: number): { x: number; y: number } => {
    let best = r.holds[0] as { x: number; y: number };
    let bd = 0x7fffffff;
    for (const p of r.holds) {
      const dd = dist2(p.x, p.y, x, y);
      if (dd < bd) {
        bd = dd;
        best = p;
      }
    }
    return best;
  };
  const toward = (p: { x: number; y: number }): { x: number; y: number } => ({ x: p.x, y: p.y > 0 ? p.y - 450 : p.y < 0 ? p.y + 450 : p.y });
  // A tilt warned or tipping: at a post (and stay).
  if (w.warn >= 0) {
    const p = toward(nearestHold(h.x, h.y));
    if (!gondolaHolds(r, h) || (s.tick < w.tip && !withinRange(h.x, h.y, p.x, p.y, 450))) {
      h.target = 0;
      return go(s, h, p.x, p.y, 120);
    }
    const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 100, (e) => targetable(s, e)), h.x, h.y);
    if (t) {
      strike(s, h, t);
      h.forced = false;
    }
    return;
  }
  const mine = nearestTo(hunters(s, h.x, h.y, 1400), h.x, h.y);
  const cable = perched(s, 2);
  cable.sort((a, b) => Math.abs(b.x) - Math.abs(a.x) || a.id - b.id);
  const t = mine ?? cable[0] ?? nearestTo(perched(s, 1), h.x, h.y) ?? nearestTo(hunters(s, h.x, h.y, 8000), h.x, h.y);
  if (!t) {
    h.target = 0;
    const p = toward(nearestHold(h.x, h.y));
    return go(s, h, p.x, p.y);
  }
  // From the post nearest to it, within reach.
  if (withinRange(h.x, h.y, t.x, t.y, h.stats.range - 200)) return strike(s, h, t);
  const p = toward(nearestHold(t.x, t.y));
  h.target = 0;
  go(s, h, p.x, p.y, 200);
};

/** The blind box bot's tries: the setting it means to make, and how many it gave up on. */
const BOX_TRY = new WeakMap<BattleState, { want: number[]; n: number; seen: number }>();

/** The obvious: brake with the biggest on C, the rest on A, B1, B2 as they come, R at a guess. */
const boxBlindTry = (s: BattleState, n: number): number[] => {
  const r = s.rules?.senda?.gearbox as NonNullable<NonNullable<NonNullable<BattleState['rules']>['senda']>['gearbox']>;
  let x = (s.seed ^ (n * 2654435761)) >>> 0;
  const rnd = (m: number): number => {
    x = (Math.imul(x ^ (x >>> 15), 2246822507) ^ n) >>> 0;
    return x % m;
  };
  const pool = [...r.pieces];
  const big = pool.reduce((m, t) => (t > m ? t : m), 0);
  pool.splice(pool.indexOf(big), 1);
  const take = (): number => pool.splice(rnd(pool.length), 1)[0] as number;
  const a = take();
  const b1 = take();
  const b2 = take();
  const rr = rnd(2) === 0 && pool.length ? take() : 0;
  return [a, b1, b2, big, rr];
};

/** One step toward a setting of the box (teeth on A, B1, B2, C, R): false when it is set. */
const boxStep = (s: BattleState, h: Hero, want: number[]): boolean => {
  const r = s.rules?.senda?.gearbox;
  const w = s.senda?.gearbox;
  if (!r || !w) return false;
  if (w.job >= 0) return true;
  const teethOn = (slot: number): number => {
    const k = boxAt(w, slot);
    return k >= 0 ? (r.pieces[k] as number) : 0;
  };
  const send = (at: number): boolean => {
    boxOrder(s, h, at);
    return true;
  };
  const order = [0, 1, 2, 3, 4];
  if (w.hand >= 0) {
    const t = r.pieces[w.hand] as number;
    for (const a of order) if ((want[a] as number) === t && teethOn(a) === 0) return send(a);
    return send(5 + w.hand);
  }
  for (const a of order) {
    const t = teethOn(a);
    if (t && t !== (want[a] as number)) return send(a);
  }
  for (const a of order) {
    const need = want[a] as number;
    if (!need || teethOn(a) === need) continue;
    for (let k = 0; k < r.pieces.length; k++) if (r.pieces[k] === need && (w.on[k] as number) >= 5) return send(5 + k);
  }
  return false;
};

/**
 * The gearbox. Who knows works the ratio out (60 × A ÷ B1 × B2 ÷ C, R for the other way), sets it gear by gear (what
 * sits on the slot it needs struck off first, the peck aimed at it stepped out of unless a gear is half changed),
 * pulls the clutch, and fights La Picotera; what flies at it or at Dunna first. Who doesn't brakes with the biggest on
 * C, the rest as they come, and pulls; wrong, it tries another the same way.
 */
const gearboxBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.gearbox;
  const w = s.senda?.gearbox;
  if (!r || !w) return;
  const d = npc(s, 'dunna');
  // Out of a peck aimed at it (unless a gear is half changed: it takes the blow).
  if (knows && w.peckSlot === -1 && withinRange(h.x, h.y, w.peckAt.x, w.peckAt.y, r.boss.peck.r + 300) && !(w.job >= 0 && w.held > 0)) {
    const dx = h.x - w.peckAt.x;
    const dy = h.y - w.peckAt.y;
    const k = imax(1, isqrt(dx * dx + dy * dy));
    const p = standNear(s, h, h.x + idiv(dx * 1300, k) - (dx === 0 && dy === 0 ? 1300 : 0), h.y + idiv(dy * 1300, k), 900);
    if (p) {
      w.job = -1;
      h.target = 0;
      return go(s, h, p.x, p.y, 100);
    }
  }
  const mine = nearestTo(hunters(s, h.x, h.y, knows ? 1500 : 2500), h.x, h.y);
  if (mine && !(w.job >= 0 && w.held > 0)) {
    w.job = -1;
    return strike(s, h, mine);
  }
  if (knows && d && d.hp > 0) {
    const on = nearestTo(hunters(s, d.x, d.y, 1800), d.x, d.y);
    if (on && !(w.job >= 0 && w.held > 0)) {
      w.job = -1;
      return reachFor(s, h, on);
    }
  }
  const boss = w.boss ? findEnemy(s, w.boss) : undefined;
  if (w.right >= 0) {
    if (boss && boss.hp > 0) return reachFor(s, h, boss);
    if (guard(s, h, h.x, h.y, 6000)) return;
    return;
  }
  let want: number[];
  if (knows) want = boxSolve(r.pieces, r.want, r.way)[0] ?? [0, 0, 0, 0, 0];
  else {
    const t = BOX_TRY.get(s) ?? { want: boxBlindTry(s, 0), n: 0, seen: 0 };
    BOX_TRY.set(s, t);
    if (w.errors > t.seen) {
      t.seen = w.errors;
      t.n++;
      t.want = boxBlindTry(s, t.n);
    }
    want = t.want;
  }
  // What sits on the slot the next step needs (or on the lever): off first.
  const sitOn = (slot: number): Enemy | undefined => {
    const gr = s.rules?.senda?.garg;
    const i = gr?.perches.findIndex((p) => p.kind === 3 && p.slot === slot) ?? -1;
    const g = i >= 0 ? gargAt(s, i) : undefined;
    return g ? (findEnemy(s, g.id) ?? undefined) : undefined;
  };
  if (w.job >= 0 && w.job !== 5 + r.pieces.length && w.job < 5) {
    const e = sitOn(w.job);
    if (e && e.hp > 0) {
      w.job = -1;
      return reachFor(s, h, e);
    }
  }
  if (boxStep(s, h, want)) return;
  const le = sitOn(5);
  if (le && le.hp > 0) return reachFor(s, h, le);
  boxOrder(s, h, boxLever(r));
};
